import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller, type FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserRole } from '@repo/shared-types';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { useTenant } from '../../hooks/queries/useTenants';
import { useApplications, useLeaseByApplication } from '../../hooks/queries/useLeasing';
import {
  AlertCircle, Send, Clock, CheckCircle, ChevronRight, ChevronLeft,
  FileText, User, Lock, Building, Shield, Briefcase, CreditCard, Upload, Info
} from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { DatePicker } from '../ui/DatePicker';
import type { LucideIcon } from 'lucide-react';
import { FormErrorSummary } from '../ui/FormErrorSummary';
import { FormLoadingOverlayInline } from '../ui/FormLoadingOverlay';
import { DraftBanner } from '../ui/DraftBanner';
import { useFormDraft } from '../../hooks/useFormDraft';
import {
  formatCurrency,
  formatDate,
  formatErrorMessage,
  getErrorWithAction,
  getPostHogInstance,
  logger,
  US_STATES,
  type StateOption,
} from '@repo/shared-utils';
import { useToast } from '../common/ToastContext';
import type { OnboardingData, OnboardingCompletionData, OnboardingStepData } from '@repo/shared-types/common/onboarding';
import type { CompleteClerkOnboardingData } from '../../services/api/auth';
import { PRIVACY_POLICY_VERSION, TERMS_OF_SERVICE_VERSION } from '../../constants/legalVersions';
import { getOnboardingIdentity } from '../../utils/onboardingIdentity';
import { captureOnboardingEvent, ONBOARDING_ANALYTICS, type OnboardingFlowKind } from '../../analytics/onboardingFunnel';
import { useOnboardingAbandonTelemetry } from '../../hooks/onboarding/useOnboardingAbandonTelemetry';
import { useMigrationUploadPollSession } from '../../hooks/onboarding/useMigrationUploadPollSession';
import { onboardingUploadApi } from '../../services/api/onboarding';
import { RpcApiError, ONBOARDING_UPLOAD_ERROR_CODE } from '../../services/api/transport';
import { redactSensitiveFormFields } from '../../utils/onboardingDraftStorage';
import { AddPropertyWizard } from '../property/AddPropertyWizard';
import { MigrationReviewContent, type ParsedPayload } from './MigrationReviewContent';
import {
  PM_STEP_IDS_SMO,
  SIGNUP_STEP_BASE,
  PM_STEP_TITLE_DESCRIPTION,
  OWNER_STEP_TITLE_DESCRIPTION,
  INVESTOR_STEP_TITLE_DESCRIPTION,
  VENDOR_STEP_TITLE_DESCRIPTION,
  getWelcomeBlurb,
  getSignupWelcomeRoleTitle,
  getOnboardingHeaderTitle,
  getOnboardingStepShortLabel,
  getOnboardingPrimaryFooterLabel,
  clampSignupStepIndex,
  transitionSignupStepIndex,
  type ManagementApproach,
} from './signupWizardMachine';

export type { ManagementApproach };

/** Merge submitted step into prior onboarding map (completion payload + unit tests). */
export function buildOnboardingCompletionStepData(
  prev: OnboardingData,
  stepId: string,
  data: Record<string, unknown>,
): OnboardingData {
  return { ...prev, [stepId]: data } as OnboardingData;
}

/** Stable DOM ids for onboarding fields (labels, `aria-describedby`, tests). */
export function onboardingFieldId(stepId: string, field: string): string {
  return `onboarding-${stepId}-${field}`;
}

/**
 * Merge DOM control values into React Hook Form before validation (e.g. Next).
 * Browser autofill can set `input.value` without firing `input`/`change`, so RHF may still hold defaults.
 */
export function syncAutofillDomToRhf(
  form: HTMLFormElement | null,
  setValue: (name: string, value: string, options?: { shouldDirty?: boolean; shouldTouch?: boolean; shouldValidate?: boolean }) => void,
  getValues: (name?: string) => unknown,
): void {
  if (!form) return;
  const elements = Array.from(form.elements);
  for (const el of elements) {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) continue;
    if (el.disabled) continue;
    const name = el.name;
    if (!name) continue;
    if (el instanceof HTMLInputElement) {
      const t = el.type;
      if (t === 'checkbox' || t === 'radio' || t === 'file' || t === 'submit' || t === 'button' || t === 'hidden') continue;
    }
    const domValue = el.value;
    const current = getValues(name);
    if (current !== null && typeof current === 'object') continue;
    const curStr = current === undefined || current === null ? '' : String(current);
    if (curStr === domValue) continue;
    setValue(name, domValue, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
  }
}

function formatMigrationUploadFailure(err: unknown): string {
  if (err instanceof RpcApiError) {
    switch (err.code) {
      case ONBOARDING_UPLOAD_ERROR_CODE.SESSION_INVALID:
        return 'Your upload session is invalid or expired. Re-upload your portfolio file.';
      case ONBOARDING_UPLOAD_ERROR_CODE.NOT_FOUND:
        return 'This upload is no longer available. Re-upload your portfolio file.';
      case ONBOARDING_UPLOAD_ERROR_CODE.CONSUMED:
        return 'This upload was already used. Start a new upload to import again.';
      case ONBOARDING_UPLOAD_ERROR_CODE.VALIDATION:
        return err.message;
      default:
        break;
    }
  }
  return err instanceof Error ? err.message : 'Upload failed.';
}

export interface SignupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  role: UserRole;
  /** When true, use Self Managing Owner flow (PM subrole): different title, steps (no company/license), optional company in personal. */
  isSelfManagingOwner?: boolean;
  /** When set from Landing Owner fork, pre-fills Owner wizard Step 2 (Management Approach). */
  initialManagementApproach?: ManagementApproach;
  onComplete?: (data: OnboardingCompletionData) => void;
  onBack?: () => void; // When set (e.g. from landing), show back button in header
  initialStep?: number;
  initialData?: OnboardingData;
  readOnly?: boolean;
  tenantId?: string;
  /** When set with role TENANT, pre-fill from this application (e.g. from invite link). */
  applicationId?: string;
  /** When true, render only wizard content (no full-screen overlay/card). Use when wizard is inside another modal (e.g. Join the Ecosystem). */
  embedded?: boolean;
  /** When provided and user completes Review step (PM), this is called with full onboarding payload instead of onComplete. Parent should save to sessionStorage and navigate to Clerk sign-up (form-first, auth-last). */
  onClerkSignupRequest?: (payload: CompleteClerkOnboardingData) => void;
  /** Called when the current step index changes (e.g. for progress headers or analytics). */
  onStepChange?: (stepIndex: number) => void;
  /**
   * When the host modal closes without using the wizard close control, the parent calls `ref.current?.()`
   * so abandonment analytics still run (deduped with other exit paths).
   */
  onboardingAbandonLatchRef?: MutableRefObject<(() => void) | null>;
}

const STEP_ICONS: Record<string, LucideIcon> = {
  lease: FileText,
  personal: User,
  emergency: AlertCircle,
  portal: Lock,
  business: Building,
  licenses: Shield,
  w9: FileText,
  services: Briefcase,
  bank: CreditCard,
  approval: CheckCircle,
  welcome: CheckCircle,
  company: Building,
  license: Shield,
  property: Building,
  review: FileText,
  'owner-profile': User,
  'property-management-approach': Building,
  tax: FileText,
  'workflow-preferences': Shield,
  'add-owner-properties': Building,
  'review-portal': Lock,
  terms: FileText,
  entity: Building,
  accreditation: Shield,
  legal: FileText,
  'migration-upload': Upload,
  'migration-review': FileText,
};

const STEPS_CONFIG: Record<UserRole, { id: string; label: string; icon: LucideIcon }[]> = (() => {
  const roles: UserRole[] = [
    UserRole.TENANT,
    UserRole.VENDOR,
    UserRole.PROPERTY_MANAGER,
    UserRole.OWNER,
    UserRole.INVESTOR,
    UserRole.ADMIN,
    UserRole.SYSTEM_ADMIN,
  ];
  const out = {} as Record<UserRole, { id: string; label: string; icon: LucideIcon }[]>;
  for (const r of roles) {
    out[r] = SIGNUP_STEP_BASE[r].map((s) => ({
      ...s,
      icon: STEP_ICONS[s.id] ?? FileText,
    }));
  }
  return out;
})();

const stepSchemas: Record<string, z.ZodSchema<unknown>> = {
  personal: z.object({
    firstName: z.string().refine((s) => s.trim().length >= 1, "First name is required"),
    lastName: z.string().refine((s) => s.trim().length >= 1, "Last name is required"),
    email: z.string()
      .refine((s) => s.trim().length >= 1, "Email is required")
      .refine((s) => z.string().email().safeParse(s.trim()).success, "Invalid email format"),
    phone: z.string().min(1, "Phone is required"),
    jobTitle: z.string().optional(),
  }),
  entity: z.object({
    firstName: z.string().refine((s) => s.trim().length >= 1, "First name is required"),
    lastName: z.string().refine((s) => s.trim().length >= 1, "Last name is required"),
    email: z.string()
      .refine((s) => s.trim().length >= 1, "Email is required")
      .refine((s) => z.string().email().safeParse(s.trim()).success, "Invalid email format"),
    phone: z.string().min(1, "Phone is required")
  }),
  /** Vendor business info (ONBOARDING_DOCUMENTATION §4). */
  business: z.object({
    businessName: z.string().refine((s) => s.trim().length >= 1, 'Legal business name is required'),
    contactFirstName: z.string().refine((s) => s.trim().length >= 1, 'Primary contact first name is required'),
    contactLastName: z.string().refine((s) => s.trim().length >= 1, 'Primary contact last name is required'),
    email: z.string()
      .refine((s) => s.trim().length >= 1, 'Business email is required')
      .refine((s) => z.string().email().safeParse(s.trim()).success, 'Invalid email format'),
    phone: z.string().refine((s) => s.trim().length >= 1, 'Business phone is required'),
    businessAddress: z.string().refine((s) => s.trim().length >= 1, 'Physical business address is required'),
    city: z.string().refine((s) => s.trim().length >= 1, 'City is required'),
    state: z.string().refine((s) => s.trim().length >= 1, 'State is required'),
    zip: z.string().refine((s) => s.trim().length >= 1, 'ZIP code is required'),
    businessType: z.string().refine(
      (s) => s?.trim() && ['Sole Proprietor', 'LLC', 'Corporation', 'Partnership'].includes(s.trim()),
      'Business entity type is required'
    ),
  }),
  /** Vendor licenses & insurance (§4). */
  licenses: z.object({
    licenseNumber: z.string().optional(),
    licenseState: z.string().optional(),
    licenseExpiration: z.string().optional(),
    generalLiability: z.any().refine(
      (v) => v != null && (v as FileList).length > 0,
      'Certificate of Insurance (COI) is required'
    ),
    insuranceAmount: z.enum(['$500,000', '$1,000,000', '$2,000,000+'], {
      required_error: 'Coverage amount is required'
    }),
    insuranceExpiration: z.string().min(1, 'Insurance expiration date is required'),
    workersComp: z.any().optional(),
  }),
  w9: z.object({
    taxIdType: z.enum(['EIN', 'SSN'], {
      required_error: "Tax ID type is required"
    }),
    taxIdNumber: z.string().min(1, "Tax ID number is required")
  }),
  bank: z.object({
    bankName: z.string().optional(),
    accountType: z.string().optional(),
    routingNumber: z.string().min(1, "Routing number is required").regex(/^\d{9}$/, "Routing number must be 9 digits"),
    accountNumber: z.string().min(1, "Account number is required"),
    confirmAccountNumber: z.string().min(1, "Please confirm account number")
  }).superRefine((data, ctx) => {
    if (data.accountNumber !== data.confirmAccountNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Account numbers do not match",
        path: ["confirmAccountNumber"]
      });
    }
  }),
  bank_owner: z.object({
    bankName: z.string().min(1, 'Bank name is required'),
    accountType: z.enum(['Checking', 'Savings'], { required_error: 'Account type is required' }),
    routingNumber: z.string().min(1, 'Routing number is required').regex(/^\d{9}$/, 'Routing number must be 9 digits'),
    accountNumber: z.string().min(1, 'Account number is required'),
    confirmAccountNumber: z.string().min(1, 'Please confirm account number'),
    distributionFrequency: z.enum(['Monthly', 'Quarterly', 'Annually'], { required_error: 'Distribution frequency is required' }),
  }).superRefine((data, ctx) => {
    if (data.accountNumber !== data.confirmAccountNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Account numbers do not match',
        path: ['confirmAccountNumber'],
      });
    }
  }),
  bank_vendor: z.object({
    bankName: z.string().min(1, 'Bank name is required'),
    accountType: z.enum(['Business Checking', 'Business Savings'], { required_error: 'Account type is required' }),
    routingNumber: z.string().min(1, 'Routing number is required').regex(/^\d{9}$/, 'Routing number must be 9 digits'),
    accountNumber: z.string().min(1, 'Account number is required'),
    confirmAccountNumber: z.string().min(1, 'Please confirm account number'),
  }).superRefine((data, ctx) => {
    if (data.accountNumber !== data.confirmAccountNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Account numbers do not match',
        path: ['confirmAccountNumber'],
      });
    }
  }),
  services: z.object({
    serviceLevel: z.enum(['full-service', 'lite', 'both'], { required_error: 'Please select a service level' }),
    fullServiceCategories: z.array(z.string()).optional(),
    liteServiceCategories: z.array(z.string()).optional(),
    services: z.array(z.string()).optional(),
    lightServices: z.boolean().optional()
  }).superRefine((data, ctx) => {
    if (data.serviceLevel === 'full-service') {
      const list = data.fullServiceCategories ?? [];
      if (list.length === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select at least one full-service category', path: ['fullServiceCategories'] });
    } else if (data.serviceLevel === 'lite') {
      const list = data.liteServiceCategories ?? [];
      if (list.length === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select at least one lite service category', path: ['liteServiceCategories'] });
    } else if (data.serviceLevel === 'both') {
      const full = (data.fullServiceCategories ?? []).length;
      const lite = (data.liteServiceCategories ?? []).length;
      if (full === 0 && lite === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select at least one category in full-service and/or lite', path: ['fullServiceCategories'] });
    }
  }),
  emergency: z.object({
    emergencyContactName: z.string().min(1, "Emergency contact name is required"),
    emergencyContactPhone: z.string().min(1, "Emergency contact phone is required"),
    emergencyContactRelation: z.string().min(1, "Relationship is required")
  }),
  portal: z.object({
    username: z.string().refine((s) => s.trim().length >= 1, "Username is required"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm password")
  }).superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords do not match",
        path: ["confirmPassword"]
      });
    }
  }),
  lease: z.object({ acknowledge: z.boolean().optional(), applicationId: z.string().optional() }),
  welcome: z.object({}),
  'create-account': z.object({}),
  company: z.object({
    companyName: z.string().refine((s) => s.trim().length >= 1, 'Company legal name is required'),
    phone: z.string().optional(),
    address: z.string().min(1, 'Business address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(1, 'ZIP code is required'),
    teamSize: z.string().min(1, 'Team size is required'),
    dba: z.string().optional(),
    website: z.string().optional(),
  }),
  /** SMO: personal required, optional company/address in one step. */
  personal_smo: z.object({
    firstName: z.string().refine((s) => s.trim().length >= 1, 'First name is required'),
    lastName: z.string().refine((s) => s.trim().length >= 1, 'Last name is required'),
    email: z.string()
      .refine((s) => s.trim().length >= 1, 'Email is required')
      .refine((s) => z.string().email().safeParse(s.trim()).success, 'Invalid email format'),
    phone: z.string().min(1, 'Phone is required'),
    companyName: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
  }),
  license: z.object({
    licenseNumber: z.string().optional(),
    licenseState: z.string().optional(),
    licenseExpiration: z.string().optional(),
    verifyLater: z.boolean().optional(),
    certifications: z.array(z.string()).optional(),
    licenseDocument: z.any().optional(),
    insuranceCOI: z.any().optional(),
  }),
  'migration-upload': z.object({ upload_token: z.string().optional() }),
  'migration-review': z.object({ confirmed: z.boolean().optional(), migrationReviewStep: z.union([z.number(), z.string()]).optional() }),
  review: z.object({
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    termsAccepted: z.boolean().optional(),
  }).superRefine((data, ctx) => {
    if (data.password != null && data.password !== '' && data.password !== data.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Passwords do not match', path: ['confirmPassword'] });
    }
  }),
  permissions: z.object({ permissionGroups: z.array(z.string()).optional() }),
  property: z.object({
    addPropertiesNow: z.enum(['yes', 'later', 'import']).optional(),
    properties: z.array(z.object({
      name: z.string().min(1, 'Property name is required'),
      address: z.string().min(1, 'Street address is required'),
      city: z.string().optional(),
      state: z.string().optional(),
      zip: z.string().optional(),
      yearBuilt: z.string().optional(),
      type: z.string().min(1, 'Property type is required'),
      status: z.string().min(1, 'Status is required'),
    })).optional(),
  }),
  tax: z.object({
    taxIdType: z.enum(['SSN', 'EIN'], { required_error: 'Tax ID type is required' }),
    taxId: z.string().min(1, 'Tax ID is required'),
    w9Upload: z.any().optional(),
  }),
  terms: z.object({ agreedToTerms: z.boolean().optional() }),
  accreditation: z.object({ status: z.string().optional(), notes: z.string().optional() }),
  legal: z.object({ signOffAcknowledged: z.boolean().optional() }),
  /** Investor terms (§5). */
  terms_investor: z.object({
    offeringCode: z.string().refine((s) => s.trim().length >= 1, 'Offering code is required'),
    investmentAmount: z.number().min(50_000, 'Minimum investment is $50,000'),
    investmentType: z.enum(['Equity', 'Debt', 'Preferred Equity', 'Mezzanine'], { required_error: 'Investment type is required' }),
  }),
  /** Investor entity (§5). */
  entity_investor: z.object({
    investorType: z.enum(['Individual', 'Trust', 'Self-Directed IRA', 'LLC', 'Corporation', 'Fund'], { required_error: 'Investor type is required' }),
    entityName: z.string().optional(),
    firstName: z.string().refine((s) => s.trim().length >= 1, 'First name is required'),
    lastName: z.string().refine((s) => s.trim().length >= 1, 'Last name is required'),
    title: z.string().optional(),
    email: z.string()
      .refine((s) => s.trim().length >= 1, 'Email is required')
      .refine((s) => z.string().email().safeParse(s.trim()).success, 'Invalid email format'),
    phone: z.string().min(1, 'Phone is required'),
    address: z.string().refine((s) => s.trim().length >= 1, 'Address is required'),
    city: z.string().refine((s) => s.trim().length >= 1, 'City is required'),
    state: z.string().refine((s) => s.trim().length >= 1, 'State is required'),
    zip: z.string().refine((s) => s.trim().length >= 1, 'ZIP code is required'),
  }).superRefine((data, ctx) => {
    const needsEntity = ['Trust', 'Self-Directed IRA', 'LLC', 'Corporation', 'Fund'].includes(data.investorType);
    if (needsEntity && (!data.entityName || data.entityName.trim().length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Entity name is required for this investor type', path: ['entityName'] });
    }
  }),
  /** Investor accreditation (§5). */
  accreditation_investor: z.object({
    accreditationType: z.string().refine((s) => s.trim().length >= 1, 'Accreditation type is required'),
    accreditationDocument: z.any().refine(
      (v) => v != null && (v as FileList).length > 0,
      'Verification document is required'
    ),
    cpaLetter: z.any().optional(),
  }),
  /** Investor bank (distributions). */
  bank_investor: z.object({
    bankName: z.string().min(1, 'Bank name is required'),
    accountType: z.enum(['Checking', 'Savings'], { required_error: 'Account type is required' }),
    routingNumber: z.string().min(1, 'Routing number is required').regex(/^\d{9}$/, 'Routing number must be 9 digits'),
    accountNumber: z.string().min(1, 'Account number is required'),
    confirmAccountNumber: z.string().min(1, 'Please confirm account number'),
  }).superRefine((data, ctx) => {
    if (data.accountNumber !== data.confirmAccountNumber) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Account numbers do not match', path: ['confirmAccountNumber'] });
    }
  }),
  /** Investor legal sign-off (§5). */
  legal_investor: z.object({
    ppmReviewed: z.boolean().refine((v) => v === true, 'You must confirm PPM review'),
    operatingAgreement: z.boolean().refine((v) => v === true, 'You must agree to the Operating Agreement'),
    subscriptionAgreement: z.boolean().refine((v) => v === true, 'You must agree to the Subscription Agreement'),
    riskAcknowledgment: z.boolean().refine((v) => v === true, 'You must acknowledge investment risks'),
    signature: z.string().refine((s) => s.trim().length >= 1, 'Electronic signature is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm password'),
    termsAccepted: z.boolean().refine((v) => v === true, 'You must agree to the Platform Terms of Service'),
  }).superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Passwords do not match', path: ['confirmPassword'] });
    }
  }),
  'owner-profile': z.object({
    firstName: z.string().refine((s) => s.trim().length >= 1, 'First name is required'),
    lastName: z.string().refine((s) => s.trim().length >= 1, 'Last name is required'),
    email: z.string()
      .refine((s) => s.trim().length >= 1, 'Email is required')
      .refine((s) => z.string().email().safeParse(s.trim()).success, 'Invalid email format'),
    phone: z.string().min(1, 'Phone is required'),
    ownershipType: z.string().optional(),
    entityName: z.string().optional(),
  }),
  'property-management-approach': z.object({
    managementStructure: z.enum(['self-manage-all', 'pm-for-all', 'mixed'], { required_error: 'Choose your management approach' }),
    linkMethod: z.string().optional(),
    inviteCode: z.string().optional(),
    searchPM: z.string().optional(),
  }),
  'workflow-preferences': z.object({
    monthlyReports: z.boolean().optional(),
    maintenanceAlerts: z.boolean().optional(),
  }),
  'add-owner-properties': z.object({
    properties: z.array(z.object({
      address: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zip: z.string().optional(),
      propertyType: z.string().optional(),
      units: z.number().optional(),
      isSelfManaged: z.boolean().optional(),
    })).optional(),
  }),
  'review-portal': z.object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm password'),
    termsAccepted: z.boolean().refine((v) => v === true, 'You must agree to the terms'),
  }).superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Passwords do not match', path: ['confirmPassword'] });
    }
  }),
  /** Vendor approval: consent, password, terms. */
  approval_vendor: z.object({
    backgroundCheck: z.boolean().refine((v) => v === true, 'You must consent to a background check'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm password'),
    termsAccepted: z.boolean().refine((v) => v === true, 'You must agree to the Terms of Service and Privacy Policy'),
  }).superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Passwords do not match', path: ['confirmPassword'] });
    }
  }),
};

/** Steps where localStorage autosave is disabled (banking, tax, passwords, upload tokens). */
const ONBOARDING_SENSITIVE_DRAFT_STEPS = new Set([
  'bank',
  'w9',
  'tax',
  'legal',
  'portal',
  'approval',
  'migration-upload',
]);

const inputClasses = "w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 focus:scale-[1.005] focus:shadow-sm disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:text-slate-500 transition-all duration-200";
const labelClasses = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1";
const cardClasses = "bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] animate-slide-up border border-slate-200 dark:border-slate-700";

const useStepForm = (stepId: string, role: UserRole, initialData: OnboardingStepData = {}, readOnly: boolean = false, isSelfManagingOwner?: boolean) => {
  const formKey = `onboarding-${role}-step-${stepId}${isSelfManagingOwner ? '-smo' : ''}`;
  const draftAutosaveEnabled = !readOnly && !ONBOARDING_SENSITIVE_DRAFT_STEPS.has(stepId);
  const schema = (stepId === 'personal' && isSelfManagingOwner)
    ? stepSchemas['personal_smo']
    : (stepId === 'bank' && role === UserRole.OWNER)
      ? stepSchemas['bank_owner']
      : (stepId === 'bank' && role === UserRole.VENDOR)
        ? stepSchemas['bank_vendor']
        : (stepId === 'bank' && role === UserRole.INVESTOR)
          ? stepSchemas['bank_investor']
          : (stepId === 'approval' && role === UserRole.VENDOR)
            ? stepSchemas['approval_vendor']
            : (role === UserRole.INVESTOR && stepId === 'terms')
              ? stepSchemas['terms_investor']
              : (role === UserRole.INVESTOR && stepId === 'entity')
                ? stepSchemas['entity_investor']
                : (role === UserRole.INVESTOR && stepId === 'accreditation')
                  ? stepSchemas['accreditation_investor']
                  : (role === UserRole.INVESTOR && stepId === 'legal')
                    ? stepSchemas['legal_investor']
                    : stepSchemas[stepId];

  const defaultValues = useMemo(() => {
    // Property step: Property Basics shape per property
    if (stepId === 'property') {
      const fromInitial = initialData as { addPropertiesNow?: string; properties?: { name?: string; address?: string; city?: string; state?: string; zip?: string; yearBuilt?: string; type?: string; status?: string }[] };
      const base = { addPropertiesNow: fromInitial?.addPropertiesNow ?? undefined as undefined | 'yes' | 'later' | 'import' };
      if (Array.isArray(fromInitial?.properties) && fromInitial.properties.length > 0) {
        return {
          ...base,
          properties: fromInitial.properties.map((p) => ({
            name: p.name ?? '',
            address: p.address ?? '',
            city: p.city ?? '',
            state: p.state ?? '',
            zip: p.zip ?? '',
            yearBuilt: p.yearBuilt ?? '',
            type: p.type ?? 'Multifamily',
            status: p.status ?? 'Stable',
          }))
        };
      }
      return { ...base, properties: [{ name: '', address: '', city: '', state: '', zip: '', yearBuilt: '', type: 'Multifamily', status: 'Stable' }] };
    }
    if (schema && initialData && Object.keys(initialData).length > 0) {
      if (stepId === 'services') {
        const d = initialData as Record<string, unknown>;
        return {
          serviceLevel: d.serviceLevel === 'lite' || d.serviceLevel === 'both' ? d.serviceLevel : 'full-service',
          fullServiceCategories: Array.isArray(d.fullServiceCategories) ? d.fullServiceCategories : [],
          liteServiceCategories: Array.isArray(d.liteServiceCategories) ? d.liteServiceCategories : [],
          services: Array.isArray(d.services) ? d.services : [],
          lightServices: typeof d.lightServices === 'boolean' ? d.lightServices : false,
        };
      }
      return initialData;
    }
    // Return empty defaults based on step type
    const defaults: Record<string, unknown> = {};
    if (stepId === 'services') {
      defaults.serviceLevel = 'full-service';
      defaults.fullServiceCategories = [];
      defaults.liteServiceCategories = [];
      defaults.services = [];
      defaults.lightServices = false;
    } else if (stepId === 'licenses') {
      defaults.licenseNumber = '';
      defaults.licenseState = '';
      defaults.licenseExpiration = '';
      defaults.insuranceAmount = '';
      defaults.insuranceExpiration = '';
    } else if (stepId === 'bank') {
      defaults.routingNumber = '';
      defaults.accountNumber = '';
      defaults.confirmAccountNumber = '';
      if (role === UserRole.OWNER) {
        defaults.bankName = '';
        defaults.accountType = 'Checking';
        defaults.distributionFrequency = 'Monthly';
      } else if (role === UserRole.VENDOR) {
        defaults.bankName = '';
        defaults.accountType = 'Business Checking';
      } else if (role === UserRole.INVESTOR) {
        defaults.bankName = '';
        defaults.accountType = 'Checking';
      } else {
        defaults.bankName = '';
        defaults.accountType = '';
      }
    } else if (stepId === 'w9') {
      defaults.taxIdType = 'EIN';
      defaults.taxIdNumber = '';
    } else if (stepId === 'portal') {
      defaults.username = '';
      defaults.password = '';
      defaults.confirmPassword = '';
    } else if (stepId === 'emergency') {
      defaults.emergencyContactName = '';
      defaults.emergencyContactPhone = '';
      defaults.emergencyContactRelation = '';
    } else if (stepId === 'lease') {
      defaults.acknowledge = false;
      defaults.applicationId = '';
    } else if (stepId === 'welcome' || stepId === 'create-account') {
      return {};
    } else if (stepId === 'review') {
      defaults.password = '';
      defaults.confirmPassword = '';
      defaults.termsAccepted = false;
      return defaults;
    } else if (stepId === 'company') {
      defaults.companyName = '';
      defaults.phone = '';
      defaults.address = '';
      defaults.city = '';
      defaults.state = '';
      defaults.dba = '';
      defaults.zip = '';
      defaults.teamSize = '';
      defaults.website = '';
    } else if (stepId === 'license') {
      defaults.licenseNumber = '';
      defaults.licenseState = '';
      defaults.licenseExpiration = '';
      defaults.verifyLater = false;
      defaults.certifications = [];
    } else if (stepId === 'permissions') {
      defaults.permissionGroups = [];
    } else if (stepId === 'tax') {
      defaults.taxIdType = 'SSN';
      defaults.taxId = '';
      defaults.w9Upload = undefined;
    } else if (stepId === 'terms' && role === UserRole.INVESTOR) {
      defaults.offeringCode = '';
      defaults.investmentAmount = 50000;
      defaults.investmentType = '';
    } else if (stepId === 'terms') {
      defaults.agreedToTerms = false;
    } else if (stepId === 'entity' && role === UserRole.INVESTOR) {
      defaults.investorType = 'Individual';
      defaults.entityName = '';
      defaults.firstName = '';
      defaults.lastName = '';
      defaults.title = '';
      defaults.email = '';
      defaults.phone = '';
      defaults.address = '';
      defaults.city = '';
      defaults.state = '';
      defaults.zip = '';
    } else if (stepId === 'accreditation' && role === UserRole.INVESTOR) {
      defaults.accreditationType = '';
    } else if (stepId === 'accreditation') {
      defaults.status = '';
      defaults.notes = '';
    } else if (stepId === 'legal' && role === UserRole.INVESTOR) {
      defaults.ppmReviewed = false;
      defaults.operatingAgreement = false;
      defaults.subscriptionAgreement = false;
      defaults.riskAcknowledgment = false;
      defaults.signature = '';
      defaults.password = '';
      defaults.confirmPassword = '';
      defaults.termsAccepted = false;
    } else if (stepId === 'legal') {
      defaults.signOffAcknowledged = false;
    } else if (stepId === 'owner-profile') {
      defaults.firstName = '';
      defaults.lastName = '';
      defaults.email = '';
      defaults.phone = '';
      defaults.ownershipType = 'individual';
      defaults.entityName = '';
    } else if (stepId === 'property-management-approach') {
      defaults.linkMethod = '';
      defaults.inviteCode = '';
      defaults.searchPM = '';
    } else if (stepId === 'workflow-preferences') {
      defaults.monthlyReports = false;
      defaults.maintenanceAlerts = false;
    } else if (stepId === 'add-owner-properties') {
      defaults.properties = [];
    } else if (stepId === 'review-portal') {
      defaults.password = '';
      defaults.confirmPassword = '';
      defaults.termsAccepted = false;
    } else if (stepId === 'migration-upload') {
      defaults.upload_token = (initialData as { upload_token?: string }).upload_token ?? '';
    } else if (stepId === 'migration-review') {
      defaults.confirmed = (initialData as { confirmed?: boolean }).confirmed ?? false;
      defaults.migrationReviewStep = (initialData as { migrationReviewStep?: number }).migrationReviewStep ?? 1;
    } else if (stepId === 'business') {
      defaults.businessName = '';
      defaults.contactFirstName = '';
      defaults.contactLastName = '';
      defaults.email = '';
      defaults.phone = '';
      defaults.businessAddress = '';
      defaults.city = '';
      defaults.state = '';
      defaults.zip = '';
      defaults.businessType = '';
    } else if (stepId === 'approval' && role === UserRole.VENDOR) {
      defaults.backgroundCheck = false;
      defaults.password = '';
      defaults.confirmPassword = '';
      defaults.termsAccepted = false;
    } else if (stepId === 'personal' && isSelfManagingOwner) {
      defaults.firstName = '';
      defaults.lastName = '';
      defaults.email = '';
      defaults.phone = '';
      defaults.companyName = '';
      defaults.address = '';
      defaults.city = '';
      defaults.state = '';
    } else if (stepId === 'personal') {
      defaults.firstName = '';
      defaults.lastName = '';
      defaults.email = '';
      defaults.phone = '';
      defaults.jobTitle = '';
    } else {
      defaults.firstName = '';
      defaults.lastName = '';
      defaults.email = '';
      defaults.phone = '';
    }
    return defaults;
  }, [stepId, initialData, schema, isSelfManagingOwner, role]);

  const formMethods = useForm({
    resolver: schema ? zodResolver(schema as z.ZodType<FieldValues>) : undefined,
    mode: 'onTouched',
    defaultValues
  });

  // Reset form only when the step changes so the form shows the right initial values for the new step. We intentionally omit defaultValues (and formMethods) from deps: including them can cause the effect to run on re-renders when defaultValues gets a new object reference, which resets the form while the user is typing and kicks focus out of inputs.
  useEffect(() => {
    formMethods.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit defaultValues to prevent reset-on-keystroke; stepId change is sufficient
  }, [stepId]);

  const { hasDraft, lastSaved, restoreDraft, clearDraft } = useFormDraft(formMethods, {
    formKey,
    enabled: draftAutosaveEnabled,
    redactDraft: (vals) => redactSensitiveFormFields(vals as Record<string, unknown>) as typeof vals,
  });

  // Manual save function for auto-save
  const saveDraft = useCallback((values: Record<string, unknown>) => {
    if (!draftAutosaveEnabled) return;
    try {
      const draftKey = `form-draft-${formKey}`;
      const metaKey = `${draftKey}-meta`;
      const payload = redactSensitiveFormFields(values);
      localStorage.setItem(draftKey, JSON.stringify(payload));
      localStorage.setItem(metaKey, JSON.stringify({ savedAt: new Date().toISOString() }));
    } catch (error) {
      logger.warn('Failed to save form draft:', error);
    }
  }, [formKey, draftAutosaveEnabled]);

  return { formMethods, hasDraft, lastSaved, restoreDraft, clearDraft, saveDraft };
};

type PropertyFormValues = {
  properties: { name: string; address: string; city: string; state: string; zip: string; yearBuilt: string; type: string; status: string }[];
};

export const SignupWizard: React.FC<SignupWizardProps> = ({
  isOpen,
  onClose,
  role,
  isSelfManagingOwner = false,
  initialManagementApproach,
  onComplete,
  onBack,
  initialStep = 0,
  initialData = {},
  readOnly = false,
  tenantId,
  applicationId: applicationIdProp,
  embedded = false,
  onClerkSignupRequest,
  onStepChange,
  onboardingAbandonLatchRef,
}) => {
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(() => initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [allStepData, setAllStepData] = useState<OnboardingData>(() => initialData);
  /** Authoritative merge snapshot; do not mirror from React state via useEffect (state can lag behind submit merges). */
  const allStepDataRef = useRef<OnboardingData>(initialData);
  const [showFullPropertyWizard, setShowFullPropertyWizard] = useState(false);
  const [collectedPropertiesForStep, setCollectedPropertiesForStep] = useState<PropertyFormValues['properties']>([]);
  const [migrationUploadStatus, setMigrationUploadStatus] = useState<'idle' | 'uploading' | 'parsing' | 'ready' | 'failed'>('idle');
  const [migrationUploadToken, setMigrationUploadToken] = useState<string>('');
  const [migrationUploadError, setMigrationUploadError] = useState<string>('');
  const [migrationReviewData, setMigrationReviewData] = useState<{
    parsed_payload?: Record<string, unknown>;
    exceptions?: { entityType?: string; error?: string; sourceId?: string; payload?: unknown }[];
  } | null>(null);
  const [migrationReviewLoading, setMigrationReviewLoading] = useState(false);
  const [migrationReviewError, setMigrationReviewError] = useState<string>('');
  const [progressLiveMessage, setProgressLiveMessage] = useState('');
  const completionStepSnapshotRef = useRef<OnboardingData | null>(null);
  const onboardingFlowCompletedRef = useRef(false);
  const lastStepIdRef = useRef('');
  const onboardingFlowKind: OnboardingFlowKind = embedded ? 'embedded' : 'standalone';

  const { data: tenant } = useTenant(tenantId || '');
  const { data: applications = [] } = useApplications();
  const application = (tenant?.applicationId ? applications.find(a => a.id === tenant.applicationId) : undefined)
    ?? (applicationIdProp ? applications.find(a => a.id === applicationIdProp) : undefined);
  const { data: leaseByApp } = useLeaseByApplication(
    role === UserRole.TENANT && application?.id ? application.id : undefined
  );

  /** Seed lease step with applicationId when opened from invite so it is pre-filled (once). */
  const hasSeededLeaseApplicationId = useRef(false);
  useEffect(() => {
    if (role !== UserRole.TENANT || !applicationIdProp || hasSeededLeaseApplicationId.current) return;
    hasSeededLeaseApplicationId.current = true;
    const applicationId = applicationIdProp;
    queueMicrotask(() => {
      setAllStepData((prev: OnboardingData) => {
        const lease = (prev.lease as { applicationId?: string } | undefined) ?? {};
        const next = { ...prev, lease: { ...lease, applicationId } };
        allStepDataRef.current = next;
        return next;
      });
    });
  }, [role, applicationIdProp]);

  /** Pre-fill from application when tenant has applicationId or invite supplies applicationId; or Owner fork. */
  const preFilledData = useMemo(() => {
    if (role === UserRole.TENANT && application) {
      const nameParts = application.applicantName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const fallbackName = tenant?.name?.split(' ') ?? [];
      return {
        personal: {
          firstName: firstName || fallbackName[0] || '',
          lastName: lastName || fallbackName.slice(1).join(' ') || '',
          email: application.email || tenant?.email || '',
          phone: application.phone || tenant?.phone || '',
          dateOfBirth: application.dateOfBirth || '',
        },
        emergency: {
          emergencyContactName: application.coSignerName || application.reference1Name || '',
          emergencyContactPhone: application.coSignerPhone || application.reference1Phone || '',
          emergencyContactRelation: application.coSignerName ? 'Co-signer' : application.reference1Relationship || 'Reference',
        },
        ...initialData
      };
    }
    if (role === UserRole.OWNER && initialManagementApproach) {
      return {
        ...initialData,
        'property-management-approach': { managementStructure: initialManagementApproach },
      };
    }
    return initialData;
  }, [role, application, tenant, initialData, initialManagementApproach]);

  const steps = useMemo(() => {
    const base = STEPS_CONFIG[role] || STEPS_CONFIG[UserRole.TENANT];
    if (role === UserRole.PROPERTY_MANAGER && isSelfManagingOwner) {
      return base.filter((s) => PM_STEP_IDS_SMO.includes(s.id as typeof PM_STEP_IDS_SMO[number]));
    }
    if (role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner) {
      const addNow = (allStepData.property as { addPropertiesNow?: string } | undefined)?.addPropertiesNow;
      if (addNow === 'import') {
        const propertyIdx = base.findIndex((s) => s.id === 'property');
        const reviewIdx = base.findIndex((s) => s.id === 'review');
        if (propertyIdx >= 0 && reviewIdx >= 0) {
          const before = base.slice(0, propertyIdx + 1);
          const after = base.slice(reviewIdx);
          return [
            ...before,
            { id: 'migration-upload', label: 'Upload Portfolio', icon: Upload },
            { id: 'migration-review', label: 'Review Migration', icon: FileText },
            ...after,
          ];
        }
      }
    }
    // PM embedded: keep all steps (welcome → … → review) for one coherent "Step 1 of 7" progress
    if (role === UserRole.OWNER) {
      const mgmt = initialManagementApproach ?? (allStepData['property-management-approach']?.managementStructure as ManagementApproach | undefined);
      const showAddProperties = mgmt === 'self-manage-all' || mgmt === 'mixed';
      return base.filter((s) => s.id !== 'add-owner-properties' || showAddProperties);
    }
    return base;
  }, [role, isSelfManagingOwner, initialManagementApproach, allStepData]);

  /** When the step list shrinks (owner PM path, PM import toggles), keep index in range before any `currentStepData.id` access. */
  useLayoutEffect(() => {
    setCurrentStep((prev) => {
      const next = clampSignupStepIndex(prev, steps.length);
      return next === prev ? prev : next;
    });
  }, [steps.length]);

  const safeStep = steps.length > 0 ? clampSignupStepIndex(currentStep, steps.length) : 0;
  const currentStepData =
    steps.length > 0
      ? steps[safeStep]
      : ({ id: 'welcome', label: 'Welcome', icon: CheckCircle } as { id: string; label: string; icon: LucideIcon });
  const isLastStep = steps.length > 0 && safeStep === steps.length - 1;

  const migrationPoll = useMigrationUploadPollSession(!!isOpen, currentStepData.id);

  const { emitAbandon } = useOnboardingAbandonTelemetry({
    enabled: !!isOpen,
    role,
    flow: onboardingFlowKind,
    getStepId: () => lastStepIdRef.current || currentStepData.id,
    getStepIndex: () => safeStep,
    isFlowComplete: () => onboardingFlowCompletedRef.current,
  });

  useEffect(() => {
    if (!onboardingAbandonLatchRef) return;
    onboardingAbandonLatchRef.current = () => emitAbandon('modal_dismiss');
    return () => {
      onboardingAbandonLatchRef.current = null;
    };
  }, [emitAbandon, onboardingAbandonLatchRef]);

  useEffect(() => {
    lastStepIdRef.current = currentStepData.id;
  }, [currentStepData.id]);

  useEffect(() => {
    if (!isOpen || readOnly) return;
    setProgressLiveMessage(`Step ${safeStep + 1} of ${steps.length}: ${currentStepData.label}`);
  }, [isOpen, readOnly, safeStep, steps.length, currentStepData.label]);

  useEffect(() => {
    captureOnboardingEvent(ONBOARDING_ANALYTICS.STEP_VIEWED, {
      role,
      flow: onboardingFlowKind,
      stepId: currentStepData.id,
      stepIndex: safeStep,
    });
  }, [safeStep, currentStepData.id, role, onboardingFlowKind]);

  const handleWizardClose = useCallback(() => {
    emitAbandon('close_button');
    onClose();
  }, [emitAbandon, onClose]);

  const emptyStep = useMemo(() => ({} as Record<string, unknown>), []);
  const stepInitialData = allStepData[currentStepData.id] ?? emptyStep;

  const { formMethods, hasDraft, lastSaved, restoreDraft, clearDraft } = useStepForm(
    currentStepData.id,
    role,
    stepInitialData,
    readOnly,
    isSelfManagingOwner
  );

  const { register, control, handleSubmit, watch, setValue, formState: { errors, isSubmitted, touchedFields }, trigger, getValues } = formMethods;
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (currentStepData?.id !== 'property') return;
    const existing = (allStepData.property as { properties?: PropertyFormValues['properties'] } | undefined)?.properties;
    if (Array.isArray(existing) && existing.length > 0 && collectedPropertiesForStep.length === 0) {
      queueMicrotask(() => setCollectedPropertiesForStep(existing));
    }
  }, [currentStepData?.id, allStepData.property, collectedPropertiesForStep.length]);

  useEffect(() => {
    if (currentStepData?.id !== 'property') return;
    setValue('properties', collectedPropertiesForStep);
  }, [currentStepData?.id, collectedPropertiesForStep, setValue]);

  useEffect(() => {
    if (currentStepData?.id !== 'migration-review') return;
    const token = (allStepData['migration-upload'] as { upload_token?: string } | undefined)?.upload_token ?? '';
    if (!token) {
      queueMicrotask(() => {
        setMigrationReviewLoading(false);
        setMigrationReviewError('');
        setMigrationReviewData(null);
      });
      return;
    }
    queueMicrotask(() => {
      setMigrationReviewLoading(true);
      setMigrationReviewError('');
    });
    let cancelled = false;
    onboardingUploadApi.result(token)
      .then((data) => {
        if (!cancelled) {
          const raw = data as { parsedPayload?: unknown; parsed_payload?: unknown; exceptions?: unknown[] };
          const parsedPayload = raw.parsed_payload ?? raw.parsedPayload;
          setMigrationReviewData({
            parsed_payload:
              parsedPayload != null && typeof parsedPayload === 'object' && !Array.isArray(parsedPayload)
                ? (parsedPayload as Record<string, unknown>)
                : undefined,
            exceptions: Array.isArray(raw.exceptions)
              ? (raw.exceptions as { entityType?: string; error?: string; sourceId?: string }[])
              : [],
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setMigrationReviewError(err instanceof Error ? err.message : 'Failed to load result.');
      })
      .finally(() => {
        if (!cancelled) setMigrationReviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentStepData?.id, allStepData['migration-upload']]);

  const migrationReviewToken = (allStepData['migration-upload'] as { upload_token?: string } | undefined)?.upload_token ?? '';
  const handleMigrationReviewDataChange = useCallback(
    (p: Record<string, unknown>, e: { entityType?: string; error?: string; sourceId?: string; payload?: unknown }[]) => {
      setMigrationReviewData((prev) => (prev ? { ...prev, parsed_payload: p, exceptions: e } : null));
      if (migrationReviewToken) {
        onboardingUploadApi.updateResult(migrationReviewToken, { parsed_payload: p, exceptions: e }).catch(() => {});
      }
    },
    [migrationReviewToken]
  );

  const focusFirstError = useCallback(() => {
    if (!formRef.current || Object.keys(errors).length === 0) return;
    const errorSummary = formRef.current.querySelector('[role="alert"]') as HTMLElement;
    if (errorSummary) {
      errorSummary.focus();
      errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const firstErrorPath = Object.keys(errors)[0];
    if (firstErrorPath) {
      const field = formRef.current.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement;
      if (field && 'focus' in field) {
        field.focus();
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [errors]);

  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length > 0) {
      setTimeout(() => focusFirstError(), 100);
    }
  }, [isSubmitted, errors, focusFirstError]);

  // Use ref to track if we've initialized; useLayoutEffect so state/ref match before paint and before async user input (setTimeout(0) could race with userEvent).
  const hasInitializedRef = useRef(false);
  useLayoutEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setCurrentStep(clampSignupStepIndex(initialStep, steps.length));
      setAllStepData(preFilledData);
      allStepDataRef.current = preFilledData;
    }
  }, [isOpen, initialStep, preFilledData, steps.length]);

  useEffect(() => {
    onStepChange?.(safeStep);
  }, [safeStep, onStepChange]);

  useEffect(() => {
    if (!isOpen || currentStepData.id !== 'migration-upload') {
      setMigrationUploadStatus((s) => (s === 'parsing' || s === 'uploading' ? 'idle' : s));
      setMigrationUploadError('');
    }
  }, [isOpen, currentStepData.id]);

  if (!isOpen) return null;

  const handleStepSubmit = async (data: Record<string, unknown>) => {
    if (currentStepData.id === 'services') {
      const full = (data.fullServiceCategories as string[] | undefined) ?? [];
      const lite = (data.liteServiceCategories as string[] | undefined) ?? [];
      data = { ...data, services: [...full, ...lite] };
    }
    if (currentStepData.id === 'migration-upload' && migrationUploadToken) {
      data = { ...data, upload_token: migrationUploadToken };
    }
    const mergedNow = buildOnboardingCompletionStepData(allStepDataRef.current, currentStepData.id, data);
    setAllStepData(mergedNow);
    allStepDataRef.current = mergedNow;

    if (currentStepData.id === 'migration-review') {
      const subStep = Number((data as { migrationReviewStep?: string | number }).migrationReviewStep) || 1;
      if (subStep < 4) return;
    }

    if (isLastStep) {
      const _finalData = mergedNow;

      // Form-first, auth last: when Clerk is used from embedded PM flow, build full payload and hand off to parent (no completion screen).
      if (onClerkSignupRequest && role === UserRole.PROPERTY_MANAGER && embedded) {
        if ((data as { termsAccepted?: boolean }).termsAccepted !== true) {
          showToast('You must accept the Terms of Service and Privacy Policy to continue.', 'error');
          return;
        }
        const personal = (_finalData.personal ?? {}) as Record<string, unknown>;
        const company = (_finalData.company ?? {}) as Record<string, unknown>;
        const companyName = typeof company.companyName === 'string' && company.companyName.trim()
          ? company.companyName.trim()
          : (typeof personal.companyName === 'string' && personal.companyName.trim() ? personal.companyName.trim() : undefined);
        const uploadToken = (_finalData['migration-upload'] as { upload_token?: string } | undefined)?.upload_token;
        const id = getOnboardingIdentity('PROPERTY_MANAGER', _finalData);
        const payload: CompleteClerkOnboardingData = {
          role: 'PROPERTY_MANAGER',
          isSelfManagingOwner: isSelfManagingOwner ?? false,
          identity: { firstName: id.firstName || undefined, lastName: id.lastName || undefined, email: id.email },
          ...(companyName && { companyName }),
          ...(uploadToken && { upload_token: uploadToken }),
          stepData: {
            personal: _finalData.personal,
            company: _finalData.company,
            license: _finalData.license,
            property: _finalData.property,
          },
          onboardingConsent: {
            termsVersion: TERMS_OF_SERVICE_VERSION,
            privacyVersion: PRIVACY_POLICY_VERSION,
            source: 'join_pm_embedded',
            termsAccepted: true,
          },
        };
        steps.forEach(step => {
          const stepFormKey = `onboarding-${role}-step-${step.id}`;
          localStorage.removeItem(`form-draft-${stepFormKey}`);
        });
        const ph = getPostHogInstance();
        ph?.capture?.('join_wizard_pm_clerk_handoff', { role: 'PROPERTY_MANAGER' });
        onboardingFlowCompletedRef.current = true;
        captureOnboardingEvent(ONBOARDING_ANALYTICS.ONBOARDING_COMPLETED, {
          role,
          flow: onboardingFlowKind,
          stepId: currentStepData.id,
          stepIndex: safeStep,
        });
        onClerkSignupRequest(payload);
        return;
      }

      try {
        completionStepSnapshotRef.current = _finalData;
        steps.forEach(step => {
          const stepFormKey = `onboarding-${role}-step-${step.id}`;
          localStorage.removeItem(`form-draft-${stepFormKey}`);
        });
        setIsCompleted(true);
        onboardingFlowCompletedRef.current = true;
      } catch (error: unknown) {
        completionStepSnapshotRef.current = null;
        const errorInfo = getErrorWithAction(error);
        showToast(formatErrorMessage(errorInfo), 'error');
      }
    } else {
      captureOnboardingEvent(ONBOARDING_ANALYTICS.STEP_COMPLETED, {
        role,
        flow: onboardingFlowKind,
        stepId: currentStepData.id,
        stepIndex: safeStep,
      });
      clearDraft();
      setCurrentStep((prev) => transitionSignupStepIndex(prev, 1, steps.length));
    }
  };

  const handleCompletionClose = (mergedOverride?: OnboardingData | null) => {
    if (onComplete) {
      // Call sites may pass a React click event if wired as `onClick={handleCompletionClose}`; ignore non-payload objects.
      const fromArg =
        mergedOverride != null &&
        typeof mergedOverride === 'object' &&
        !('_reactName' in mergedOverride)
          ? mergedOverride
          : undefined;
      const stepPayload =
        fromArg ?? completionStepSnapshotRef.current ?? allStepDataRef.current ?? allStepData;
      completionStepSnapshotRef.current = null;
      const finalData: OnboardingCompletionData = {
        role,
        stepData: { ...stepPayload },
        completedAt: new Date().toISOString(),
      };
      captureOnboardingEvent(ONBOARDING_ANALYTICS.ONBOARDING_COMPLETED, {
        role,
        flow: onboardingFlowKind,
        stepId: 'completion',
        stepIndex: safeStep,
      });
      onComplete?.(finalData);
    }
    onClose();
  };

  const handleNext = async () => {
    if (readOnly) return;

    syncAutofillDomToRhf(
      formRef.current,
      (name, value, opts) => {
        setValue(name as never, value as never, opts);
      },
      (name) => getValues(name as never),
    );

    const isValid = await trigger();
    if (!isValid) {
      const firstKey = Object.keys(formMethods.formState.errors)[0];
      captureOnboardingEvent(ONBOARDING_ANALYTICS.STEP_VALIDATION_FAILED, {
        role,
        flow: onboardingFlowKind,
        stepId: currentStepData.id,
        stepIndex: safeStep,
        errorField: firstKey,
        errorCode: 'validation',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const values = getValues();
      await handleStepSubmit(values);
    } catch (err: unknown) {
      showToast(formatErrorMessage(getErrorWithAction(err)), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (safeStep > 0) {
      const values = getValues();
      setAllStepData((prev: OnboardingData) => {
        const next = { ...prev, [currentStepData.id]: values } as OnboardingData;
        allStepDataRef.current = next;
        return next;
      });
      setCurrentStep((prev) => transitionSignupStepIndex(prev, -1, steps.length));
    }
  };

  const handleSendReminder = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Reminder sent to vendor!");
      onboardingFlowCompletedRef.current = true;
      handleWizardClose();
    }, 1000);
  };

  const renderStepContent = () => {
    switch (currentStepData.id) {
      case 'welcome': {
        const welcomeBlurbByRole = getWelcomeBlurb(role, !!isSelfManagingOwner);
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in max-w-2xl flex-shrink-0">
            <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">
              You&apos;re signing up as a{' '}
              <strong className="font-semibold text-slate-900 dark:text-white">{getSignupWelcomeRoleTitle(role, !!isSelfManagingOwner)}</strong>
              <span className="inline-flex items-center align-middle ml-1.5">
                <span className="relative group inline-flex shrink-0" title="What to expect">
                  <Info className="w-4 h-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-help" aria-label="What to expect" />
                  <span className="absolute left-0 bottom-full mb-1.5 w-72 p-3 bg-slate-900 dark:bg-slate-800 text-white text-xs leading-relaxed rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-[100] pointer-events-none">
                    {welcomeBlurbByRole}
                    <span className="absolute left-4 -bottom-1 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800" />
                  </span>
                </span>
              </span>
              .
            </p>
            <p className="text-base text-slate-600 dark:text-slate-400">Use the button below to continue.</p>
          </form>
        );
      }
      // --- COMMON STEPS ---
      case 'personal':
      case 'entity':
        if (role === UserRole.INVESTOR && currentStepData.id === 'entity') {
          const investorType = watch('investorType') as string;
          const showEntityName = ['Trust', 'Self-Directed IRA', 'LLC', 'Corporation', 'Fund'].includes(investorType);
          return (
            <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
              {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
                <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
              )}
              <div>
                <label className={labelClasses}>Investor type <span className="text-red-500">*</span></label>
                <select
                  disabled={readOnly}
                  {...register('investorType')}
                  className={`${inputClasses} ${errors.investorType ? 'border-red-500' : ''}`}
                >
                  <option value="Individual">Individual</option>
                  <option value="Trust">Trust</option>
                  <option value="Self-Directed IRA">Self-Directed IRA</option>
                  <option value="LLC">LLC</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Fund">Fund</option>
                </select>
                {errors.investorType && <p className="text-xs text-red-500 mt-1">{(errors.investorType as { message?: string }).message}</p>}
              </div>
              {showEntityName && (
                <div>
                  <label className={labelClasses}>Entity name <span className="text-red-500">*</span></label>
                  <input
                    disabled={readOnly}
                    {...register('entityName')}
                    type="text"
                    autoComplete="organization"
                    className={`${inputClasses} ${errors.entityName ? 'border-red-500' : ''}`}
                    placeholder="Legal entity name"
                  />
                  {errors.entityName && <p className="text-xs text-red-500 mt-1">{(errors.entityName as { message?: string }).message}</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'firstName')}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={onboardingFieldId(currentStepData.id, 'firstName')}
                    disabled={readOnly}
                    {...register('firstName')}
                    type="text"
                    autoComplete="given-name"
                    className={`${inputClasses} ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="Jane"
                    aria-invalid={errors.firstName ? true : undefined}
                    aria-describedby={errors.firstName ? `${onboardingFieldId(currentStepData.id, 'firstName')}-err` : undefined}
                  />
                  {errors.firstName && (
                    <p id={`${onboardingFieldId(currentStepData.id, 'firstName')}-err`} className="text-xs text-red-500 mt-1">
                      {(errors.firstName as { message?: string }).message}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'lastName')}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={onboardingFieldId(currentStepData.id, 'lastName')}
                    disabled={readOnly}
                    {...register('lastName')}
                    type="text"
                    autoComplete="family-name"
                    className={`${inputClasses} ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Doe"
                    aria-invalid={errors.lastName ? true : undefined}
                    aria-describedby={errors.lastName ? `${onboardingFieldId(currentStepData.id, 'lastName')}-err` : undefined}
                  />
                  {errors.lastName && (
                    <p id={`${onboardingFieldId(currentStepData.id, 'lastName')}-err`} className="text-xs text-red-500 mt-1">
                      {(errors.lastName as { message?: string }).message}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className={labelClasses}>Title (optional)</label>
                <input disabled={readOnly} {...register('title')} type="text" autoComplete="honorific-prefix" className={inputClasses} placeholder="If investing on behalf of entity" />
              </div>
              <div>
                <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'email')}>
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id={onboardingFieldId(currentStepData.id, 'email')}
                  disabled={readOnly}
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className={`${inputClasses} ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="jane@example.com"
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={errors.email ? `${onboardingFieldId(currentStepData.id, 'email')}-err` : undefined}
                />
                {errors.email && (
                  <p id={`${onboardingFieldId(currentStepData.id, 'email')}-err`} className="text-xs text-red-500 mt-1">
                    {(errors.email as { message?: string }).message}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'phone')}>
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  id={onboardingFieldId(currentStepData.id, 'phone')}
                  disabled={readOnly}
                  {...register('phone')}
                  type="tel"
                  autoComplete="tel"
                  className={`${inputClasses} ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                  aria-invalid={errors.phone ? true : undefined}
                  aria-describedby={errors.phone ? `${onboardingFieldId(currentStepData.id, 'phone')}-err` : undefined}
                />
                {errors.phone && (
                  <p id={`${onboardingFieldId(currentStepData.id, 'phone')}-err`} className="text-xs text-red-500 mt-1">
                    {(errors.phone as { message?: string }).message}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClasses}>Address <span className="text-red-500">*</span></label>
                <input disabled={readOnly} {...register('address')} type="text" autoComplete="street-address" className={`${inputClasses} ${errors.address ? 'border-red-500' : ''}`} placeholder="123 Main St" />
                {errors.address && <p className="text-xs text-red-500 mt-1">{(errors.address as { message?: string }).message}</p>}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClasses}>City <span className="text-red-500">*</span></label>
                  <input disabled={readOnly} {...register('city')} type="text" autoComplete="address-level2" className={`${inputClasses} ${errors.city ? 'border-red-500' : ''}`} placeholder="Portland" />
                  {errors.city && <p className="text-xs text-red-500 mt-1">{(errors.city as { message?: string }).message}</p>}
                </div>
                <div>
                  <label className={labelClasses}>State <span className="text-red-500">*</span></label>
                  <select disabled={readOnly} {...register('state')} autoComplete="address-level1" className={`${inputClasses} ${errors.state ? 'border-red-500' : ''}`}>
                    <option value="">Select state</option>
                    {US_STATES.map((s: StateOption) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {errors.state && <p className="text-xs text-red-500 mt-1">{(errors.state as { message?: string }).message}</p>}
                </div>
                <div>
                  <label className={labelClasses}>ZIP <span className="text-red-500">*</span></label>
                  <input disabled={readOnly} {...register('zip')} type="text" autoComplete="postal-code" className={`${inputClasses} ${errors.zip ? 'border-red-500' : ''}`} placeholder="97201" />
                  {errors.zip && <p className="text-xs text-red-500 mt-1">{(errors.zip as { message?: string }).message}</p>}
                </div>
              </div>
            </form>
          );
        }
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'firstName')}>
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  id={onboardingFieldId(currentStepData.id, 'firstName')}
                  disabled={readOnly}
                  {...register('firstName')}
                  type="text"
                  autoComplete="given-name"
                  className={`${inputClasses} ${errors.firstName ? 'border-red-500' : ''}`}
                  placeholder="Jane"
                  aria-invalid={errors.firstName ? true : undefined}
                  aria-describedby={errors.firstName ? `${onboardingFieldId(currentStepData.id, 'firstName')}-err` : undefined}
                />
                {errors.firstName ? (
                  <p id={`${onboardingFieldId(currentStepData.id, 'firstName')}-err`} className="text-xs text-red-500 mt-1">
                    {errors.firstName.message as string}
                  </p>
                ) : null}
              </div>
              <div>
                <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'lastName')}>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  id={onboardingFieldId(currentStepData.id, 'lastName')}
                  disabled={readOnly}
                  {...register('lastName')}
                  type="text"
                  autoComplete="family-name"
                  className={`${inputClasses} ${errors.lastName ? 'border-red-500' : ''}`}
                  placeholder="Doe"
                  aria-invalid={errors.lastName ? true : undefined}
                  aria-describedby={errors.lastName ? `${onboardingFieldId(currentStepData.id, 'lastName')}-err` : undefined}
                />
                {errors.lastName ? (
                  <p id={`${onboardingFieldId(currentStepData.id, 'lastName')}-err`} className="text-xs text-red-500 mt-1">
                    {errors.lastName.message as string}
                  </p>
                ) : null}
              </div>
            </div>
            <div>
              <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'email')}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id={onboardingFieldId(currentStepData.id, 'email')}
                disabled={readOnly}
                {...register('email')}
                type="email"
                autoComplete="email"
                className={`${inputClasses} ${errors.email ? 'border-red-500' : ''}`}
                placeholder="jane@example.com"
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? `${onboardingFieldId(currentStepData.id, 'email')}-err` : undefined}
              />
              {errors.email ? (
                <p id={`${onboardingFieldId(currentStepData.id, 'email')}-err`} className="text-xs text-red-500 mt-1">
                  {errors.email.message as string}
                </p>
              ) : null}
            </div>
            <div>
              <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'phone')}>
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                id={onboardingFieldId(currentStepData.id, 'phone')}
                disabled={readOnly}
                {...register('phone')}
                type="tel"
                autoComplete="tel"
                className={`${inputClasses} ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="(555) 123-4567"
                aria-invalid={errors.phone ? true : undefined}
                aria-describedby={errors.phone ? `${onboardingFieldId(currentStepData.id, 'phone')}-err` : undefined}
              />
              {errors.phone ? (
                <p id={`${onboardingFieldId(currentStepData.id, 'phone')}-err`} className="text-xs text-red-500 mt-1">
                  {errors.phone.message as string}
                </p>
              ) : null}
            </div>
            {role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner && (
              <div>
                <label className={labelClasses} htmlFor={onboardingFieldId(currentStepData.id, 'jobTitle')}>
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  id={onboardingFieldId(currentStepData.id, 'jobTitle')}
                  disabled={readOnly}
                  {...register('jobTitle', { required: 'Job title is required' })}
                  type="text"
                  autoComplete="organization-title"
                  className={`${inputClasses} ${errors.jobTitle ? 'border-red-500' : ''}`}
                  placeholder="e.g., Property Manager, Broker."
                  aria-invalid={errors.jobTitle ? true : undefined}
                  aria-describedby={errors.jobTitle ? `${onboardingFieldId(currentStepData.id, 'jobTitle')}-err` : undefined}
                />
                {errors.jobTitle ? (
                  <p id={`${onboardingFieldId(currentStepData.id, 'jobTitle')}-err`} className="text-xs text-red-500 mt-1">
                    {(errors.jobTitle as { message?: string }).message}
                  </p>
                ) : null}
              </div>
            )}
            {isSelfManagingOwner && (
              <>
                <div>
                  <label className={labelClasses}>Business or property name (optional)</label>
                  <input disabled={readOnly} {...register('companyName')} type="text" autoComplete="organization" className={inputClasses} placeholder="e.g. Maple Grove LLC" />
                </div>
                <div>
                  <label className={labelClasses}>Address (optional)</label>
                  <input disabled={readOnly} {...register('address')} type="text" autoComplete="street-address" className={inputClasses} placeholder="123 Main St" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClasses}>City (optional)</label>
                    <input disabled={readOnly} {...register('city')} type="text" autoComplete="address-level2" className={inputClasses} placeholder="Portland" />
                  </div>
                  <div>
                    <label className={labelClasses}>State (optional)</label>
                    <select disabled={readOnly} {...register('state')} autoComplete="address-level1" className={inputClasses}>
                      <option value="">Select state</option>
                      {US_STATES.map((s: StateOption) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </form>
        );

      // --- OWNER FLOW (Figma) ---
      case 'owner-profile':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>First Name <span className="text-red-500">*</span></label>
                <input disabled={readOnly} {...register('firstName')} type="text" autoComplete="given-name" className={`${inputClasses} ${errors.firstName ? 'border-red-500' : ''}`} placeholder="Jane" />
                {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName.message as string}</p>}
              </div>
              <div>
                <label className={labelClasses}>Last Name <span className="text-red-500">*</span></label>
                <input disabled={readOnly} {...register('lastName')} type="text" autoComplete="family-name" className={`${inputClasses} ${errors.lastName ? 'border-red-500' : ''}`} placeholder="Doe" />
                {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName.message as string}</p>}
              </div>
            </div>
            <div>
              <label className={labelClasses}>Email <span className="text-red-500">*</span></label>
              <input disabled={readOnly} {...register('email')} type="email" autoComplete="email" className={`${inputClasses} ${errors.email ? 'border-red-500' : ''}`} placeholder="jane@example.com" />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Phone <span className="text-red-500">*</span></label>
              <input disabled={readOnly} {...register('phone')} type="tel" autoComplete="tel" className={`${inputClasses} ${errors.phone ? 'border-red-500' : ''}`} placeholder="(555) 123-4567" />
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Ownership structure</label>
              <select disabled={readOnly} {...register('ownershipType')} className={inputClasses}>
                <option value="individual">Individual</option>
                <option value="llc">LLC</option>
                <option value="trust">Trust</option>
                <option value="corporation">Corporation</option>
                <option value="partnership">Partnership</option>
              </select>
            </div>
            {['llc', 'trust', 'corporation', 'partnership'].includes(watch('ownershipType') as string) && (
              <div>
                <label className={labelClasses}>Entity name (if applicable)</label>
                <input disabled={readOnly} {...register('entityName')} type="text" autoComplete="organization" className={inputClasses} placeholder="Entity name" />
              </div>
            )}
          </form>
        );

      case 'property-management-approach': {
        const mgmt = watch('managementStructure') as string | undefined;
        const showPMLink = mgmt === 'pm-for-all' || mgmt === 'mixed';
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <p className="text-sm text-slate-600 dark:text-slate-400">How do you manage your properties? You can adjust this per property later.</p>
            <div className="space-y-3">
              {(['self-manage-all', 'pm-for-all', 'mixed'] as const).map((value) => (
                <label key={value} className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <input type="radio" disabled={readOnly} {...register('managementStructure')} value={value} className="mt-1 w-4 h-4 text-brand-600" />
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {value === 'self-manage-all' && "I manage all my properties myself"}
                    {value === 'pm-for-all' && "I use a property management company for all properties"}
                    {value === 'mixed' && "Mixed: I manage some myself and use a PM for others"}
                  </span>
                </label>
              ))}
            </div>
            {errors.managementStructure && <p className="text-xs text-red-500 mt-1">{errors.managementStructure.message as string}</p>}
            {showPMLink && (
              <>
                <div className="pt-2">
                  <label className={labelClasses}>How would you like to connect with your PM?</label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" disabled={readOnly} {...register('linkMethod')} value="invite" className="w-4 h-4 text-brand-600" />
                      <span className="text-sm">I have an invite code from my PM</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" disabled={readOnly} {...register('linkMethod')} value="search" className="w-4 h-4 text-brand-600" />
                      <span className="text-sm">Search for my PM company</span>
                    </label>
                  </div>
                </div>
                {watch('linkMethod') === 'invite' && (
                  <div>
                    <label className={labelClasses}>PM invite code</label>
                    <input disabled={readOnly} {...register('inviteCode')} type="text" className={inputClasses} placeholder="Provided by your PM" />
                  </div>
                )}
                {watch('linkMethod') === 'search' && (
                  <div>
                    <label className={labelClasses}>Search PM company</label>
                    <input disabled={readOnly} {...register('searchPM')} type="text" className={inputClasses} placeholder="Company name or license number" />
                  </div>
                )}
              </>
            )}
          </form>
        );
      }

      case 'workflow-preferences':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            <p className="text-sm text-slate-600 dark:text-slate-400">Choose how you want to stay informed. Work-order approval thresholds are set later per property.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input disabled={readOnly} type="checkbox" {...register('monthlyReports')} className="w-4 h-4 rounded border-slate-300" />
              <span className="text-sm text-slate-700 dark:text-slate-300">Send me monthly financial reports</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input disabled={readOnly} type="checkbox" {...register('maintenanceAlerts')} className="w-4 h-4 rounded border-slate-300" />
              <span className="text-sm text-slate-700 dark:text-slate-300">Alert me for all maintenance requests</span>
            </label>
          </form>
        );

      case 'add-owner-properties': {
        const ownerMgmt = allStepData['property-management-approach']?.managementStructure as string | undefined ?? initialManagementApproach;
        const isMixed = ownerMgmt === 'mixed';
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {isMixed ? "Add properties and choose whether each is self-managed or PM-managed." : "Add the properties you own. All will be marked as self-managed."}
            </p>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
              <p className="text-sm text-slate-600 dark:text-slate-400">Property onboarding can be completed after you create your account from the Property Directory.</p>
              <p className="text-xs text-slate-500 mt-2">You can skip this step and add properties later.</p>
            </div>
          </form>
        );
      }

      case 'review-portal':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <p className="text-sm text-slate-600 dark:text-slate-400">Review your information in the summary below. Set a password to create your account.</p>
            <div>
              <label className={labelClasses}>Password <span className="text-red-500">*</span></label>
              <input disabled={readOnly} {...register('password')} type="password" autoComplete="new-password" className={`${inputClasses} ${errors.password ? 'border-red-500' : ''}`} placeholder="At least 8 characters" />
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Confirm password <span className="text-red-500">*</span></label>
              <input disabled={readOnly} {...register('confirmPassword')} type="password" autoComplete="new-password" className={`${inputClasses} ${errors.confirmPassword ? 'border-red-500' : ''}`} placeholder="Re-enter password" />
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword.message as string}</p>}
            </div>
            <div className="flex items-start gap-2">
              <input
                id="review-portal-termsAccepted"
                disabled={readOnly}
                type="checkbox"
                aria-describedby={
                  [errors.termsAccepted ? 'review-portal-termsAccepted-error' : null, 'review-portal-consent-desc']
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                {...register('termsAccepted')}
                className="w-4 h-4 rounded border-slate-300 mt-0.5 shrink-0"
              />
              <div id="review-portal-consent-desc" className="text-sm text-slate-700 dark:text-slate-300">
                <label htmlFor="review-portal-termsAccepted" className="cursor-pointer">
                  I agree to the
                </label>{' '}
                <a
                  href={ROUTES.LEGAL_TERMS}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href={ROUTES.LEGAL_PRIVACY}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                <span className="text-red-500"> *</span>
              </div>
            </div>
            {errors.termsAccepted && (
              <p id="review-portal-termsAccepted-error" className="text-xs text-red-500 mt-1" role="alert">
                {errors.termsAccepted.message as string}
              </p>
            )}
          </form>
        );

      // --- VENDOR SPECIFIC ---
      case 'business':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div>
              <label className={labelClasses}>Legal business name <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('businessName')}
                type="text"
                autoComplete="organization"
                className={`${inputClasses} ${errors.businessName ? 'border-red-500' : ''}`}
                placeholder="Legal business name"
              />
              {errors.businessName && <p className="text-xs text-red-500 mt-1">{errors.businessName.message as string}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Primary contact first name <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('contactFirstName')}
                  type="text"
                  autoComplete="given-name"
                  className={`${inputClasses} ${errors.contactFirstName ? 'border-red-500' : ''}`}
                  placeholder="First name"
                />
                {errors.contactFirstName && <p className="text-xs text-red-500 mt-1">{errors.contactFirstName.message as string}</p>}
              </div>
              <div>
                <label className={labelClasses}>Primary contact last name <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('contactLastName')}
                  type="text"
                  autoComplete="family-name"
                  className={`${inputClasses} ${errors.contactLastName ? 'border-red-500' : ''}`}
                  placeholder="Last name"
                />
                {errors.contactLastName && <p className="text-xs text-red-500 mt-1">{errors.contactLastName.message as string}</p>}
              </div>
            </div>
            <div>
              <label className={labelClasses}>Business email address <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('email')}
                type="email"
                autoComplete="email"
                className={`${inputClasses} ${errors.email ? 'border-red-500' : ''}`}
                placeholder="Business email address"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Business phone number <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('phone')}
                type="tel"
                autoComplete="tel"
                className={`${inputClasses} ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="Business phone number"
              />
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Physical business address <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('businessAddress')}
                type="text"
                autoComplete="street-address"
                className={`${inputClasses} ${errors.businessAddress ? 'border-red-500' : ''}`}
                placeholder="Physical business address"
              />
              {errors.businessAddress && <p className="text-xs text-red-500 mt-1">{errors.businessAddress.message as string}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>City <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('city')}
                  type="text"
                  autoComplete="address-level2"
                  className={`${inputClasses} ${errors.city ? 'border-red-500' : ''}`}
                  placeholder="City"
                />
                {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city.message as string}</p>}
              </div>
              <div>
                <label className={labelClasses}>State <span className="text-red-500">*</span></label>
                <select
                  disabled={readOnly}
                  {...register('state')}
                  autoComplete="address-level1"
                  className={`${inputClasses} ${errors.state ? 'border-red-500' : ''}`}
                >
                  <option value="">Select state</option>
                  {US_STATES.map((s: StateOption) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                {errors.state && <p className="text-xs text-red-500 mt-1">{errors.state.message as string}</p>}
              </div>
            </div>
            <div>
              <label className={labelClasses}>ZIP code <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('zip')}
                type="text"
                autoComplete="postal-code"
                className={`${inputClasses} ${errors.zip ? 'border-red-500' : ''}`}
                placeholder="ZIP code"
              />
              {errors.zip && <p className="text-xs text-red-500 mt-1">{errors.zip.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Business entity type <span className="text-red-500">*</span></label>
              <select
                disabled={readOnly}
                {...register('businessType')}
                className={`${inputClasses} ${errors.businessType ? 'border-red-500' : ''}`}
              >
                <option value="">Select business type</option>
                <option value="Sole Proprietor">Sole Proprietor</option>
                <option value="LLC">LLC</option>
                <option value="Corporation">Corporation</option>
                <option value="Partnership">Partnership</option>
              </select>
              {errors.businessType && <p className="text-xs text-red-500 mt-1">{errors.businessType.message as string}</p>}
            </div>
          </form>
        );

      // --- PM: Company (Figma: Company Profile) ---
      case 'company':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div>
              <label className={labelClasses}>Company Legal Name <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('companyName')}
                type="text"
                autoComplete="organization"
                className={`${inputClasses} ${errors.companyName ? 'border-red-500' : ''}`}
                placeholder="e.g. Acme Property Management"
              />
              {errors.companyName && <p className="text-xs text-red-500 mt-1">{(errors.companyName as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>DBA (if different)</label>
              <input disabled={readOnly} {...register('dba')} type="text" autoComplete="organization-title" className={inputClasses} placeholder="Doing Business As" />
            </div>
            <div>
              <label className={labelClasses}>Business Address <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('address')}
                type="text"
                autoComplete="street-address"
                className={`${inputClasses} ${errors.address ? 'border-red-500' : ''}`}
                placeholder="123 Main St"
              />
              {errors.address && <p className="text-xs text-red-500 mt-1">{(errors.address as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>City <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('city')}
                type="text"
                autoComplete="address-level2"
                className={`${inputClasses} ${errors.city ? 'border-red-500' : ''}`}
                placeholder="City"
              />
              {errors.city && <p className="text-xs text-red-500 mt-1">{(errors.city as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>State <span className="text-red-500">*</span></label>
              <select
                disabled={readOnly}
                {...register('state')}
                autoComplete="address-level1"
                className={`${inputClasses} ${errors.state ? 'border-red-500' : ''}`}
              >
                <option value="">Select an option</option>
                {US_STATES.map((s: StateOption) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {errors.state && <p className="text-xs text-red-500 mt-1">{(errors.state as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>ZIP Code <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('zip')}
                type="text"
                autoComplete="postal-code"
                className={`${inputClasses} ${errors.zip ? 'border-red-500' : ''}`}
                placeholder="ZIP"
              />
              {errors.zip && <p className="text-xs text-red-500 mt-1">{(errors.zip as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Team Size <span className="text-red-500">*</span></label>
              <select
                disabled={readOnly}
                {...register('teamSize')}
                className={`${inputClasses} ${errors.teamSize ? 'border-red-500' : ''}`}
              >
                <option value="">Select an option</option>
                <option value="1">Just me</option>
                <option value="2-5">2-5 people</option>
                <option value="6-10">6-10 people</option>
                <option value="11-25">11-25 people</option>
                <option value="26+">26+ people</option>
              </select>
              {errors.teamSize && <p className="text-xs text-red-500 mt-1">{(errors.teamSize as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Website</label>
              <input disabled={readOnly} {...register('website')} type="url" autoComplete="url" className={inputClasses} placeholder="https://yourcompany.com" />
            </div>
          </form>
        );

      case 'licenses':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {readOnly && (
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200 flex items-center mb-4">
                <Clock size={16} className="mr-2" />
                Vendor is currently working on this step.
              </div>
            )}
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div>
              <label className={labelClasses}>License number</label>
              <input
                disabled={readOnly}
                {...register('licenseNumber')}
                type="text"
                className={inputClasses}
                placeholder="e.g. 12345"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Trade license, contractor license, etc.</p>
            </div>
            <div>
              <label className={labelClasses}>License state</label>
              <select disabled={readOnly} {...register('licenseState')} className={inputClasses}>
                <option value="">Select state</option>
                {US_STATES.map((s: StateOption) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Controller
                control={control}
                name="licenseExpiration"
                render={({ field }) => (
                  <DatePicker
                    label="License expiration"
                    disabled={readOnly}
                    value={typeof field.value === 'string' ? field.value : ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    name={field.name}
                    onBlur={field.onBlur}
                    className={inputClasses}
                  />
                )}
              />
            </div>
            <div>
              <label className={labelClasses}>General liability (COI) <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                {...register('generalLiability')}
                className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-200"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Certificate of Insurance (COI)</p>
              {errors.generalLiability && <p className="text-xs text-red-500 mt-1">{(errors.generalLiability as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Coverage amount <span className="text-red-500">*</span></label>
              <select
                disabled={readOnly}
                {...register('insuranceAmount')}
                className={`${inputClasses} ${errors.insuranceAmount ? 'border-red-500' : ''}`}
              >
                <option value="">Select amount</option>
                <option value="$500,000">$500,000</option>
                <option value="$1,000,000">$1,000,000</option>
                <option value="$2,000,000+">$2,000,000+</option>
              </select>
              {errors.insuranceAmount && <p className="text-xs text-red-500 mt-1">{(errors.insuranceAmount as { message?: string }).message}</p>}
            </div>
            <div>
              <Controller
                control={control}
                name="insuranceExpiration"
                render={({ field }) => (
                  <DatePicker
                    label="Insurance expiration *"
                    disabled={readOnly}
                    value={typeof field.value === 'string' ? field.value : ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    name={field.name}
                    onBlur={field.onBlur}
                    error={errors.insuranceExpiration ? (errors.insuranceExpiration as { message?: string }).message : undefined}
                    className={`${inputClasses} ${errors.insuranceExpiration ? 'border-red-500' : ''}`}
                  />
                )}
              />
            </div>
            <div>
              <label className={labelClasses}>Workers&apos; comp (optional)</label>
              <input
                disabled={readOnly}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                {...register('workersComp')}
                className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-200"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Required if you have employees</p>
            </div>
          </form>
        );

      case 'w9':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">We need your tax information to process payments.</p>
              <button disabled={readOnly} type="button" className="text-brand-600 dark:text-brand-400 text-sm font-bold hover:underline disabled:text-slate-400 disabled:no-underline">Download Blank W-9</button>
            </div>
            <div>
              <label className={labelClasses}>Tax ID Type <span className="text-red-500">*</span></label>
              <div className="flex space-x-4 text-slate-900 dark:text-white">
                <label className="flex items-center">
                  <input
                    disabled={readOnly}
                    {...register("taxIdType")}
                    type="radio"
                    value="EIN"
                    className="mr-2 accent-brand-600"
                  /> EIN
                </label>
                <label className="flex items-center">
                  <input
                    disabled={readOnly}
                    {...register("taxIdType")}
                    type="radio"
                    value="SSN"
                    className="mr-2 accent-brand-600"
                  /> SSN
                </label>
              </div>
              {errors.taxIdType && <p className="text-xs text-red-500 mt-1">{errors.taxIdType.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Tax ID Number <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register("taxIdNumber")}
                type="text"
                className={`${inputClasses} ${errors.taxIdNumber ? 'border-red-500' : ''}`}
                placeholder="XX-XXXXXXX"
              />
              {errors.taxIdNumber && <p className="text-xs text-red-500 mt-1">{errors.taxIdNumber.message as string}</p>}
            </div>
            <div className={`border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-4 flex items-center justify-center transition ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
              <Upload className="text-slate-400 mr-2" size={18} />
              <span className="text-sm text-slate-600 dark:text-slate-300">Upload Signed W-9</span>
            </div>
          </form>
        );

      case 'bank':
        {
          const isOwnerBank = role === UserRole.OWNER;
          const isVendorBank = role === UserRole.VENDOR;
          const isInvestorBank = role === UserRole.INVESTOR;
          return (
            <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
              {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
                <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
              )}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 flex items-start">
                <Lock size={18} className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  Banking details are encrypted and stored securely via Stripe Connect.
                </p>
              </div>
              {(isOwnerBank || role === UserRole.VENDOR || role === UserRole.INVESTOR) && (
                <>
                  <div>
                    <label className={labelClasses}>
                      Bank Name {(isOwnerBank || isVendorBank || isInvestorBank) && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      disabled={readOnly}
                      {...register('bankName')}
                      type="text"
                      className={`${inputClasses} ${errors.bankName ? 'border-red-500' : ''}`}
                      placeholder="e.g. Chase"
                    />
                    {errors.bankName && <p className="text-xs text-red-500 mt-1">{(errors.bankName as { message?: string }).message}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>
                      Account Type {(isOwnerBank || isVendorBank || isInvestorBank) && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      disabled={readOnly}
                      {...register('accountType')}
                      className={`${inputClasses} ${errors.accountType ? 'border-red-500' : ''}`}
                    >
                      {isOwnerBank || isInvestorBank ? (
                        <>
                          <option value="">Select...</option>
                          <option value="Checking">Checking</option>
                          <option value="Savings">Savings</option>
                        </>
                      ) : isVendorBank ? (
                        <>
                          <option value="">Select...</option>
                          <option value="Business Checking">Business Checking</option>
                          <option value="Business Savings">Business Savings</option>
                        </>
                      ) : null}
                    </select>
                    {errors.accountType && <p className="text-xs text-red-500 mt-1">{(errors.accountType as { message?: string }).message}</p>}
                  </div>
                </>
              )}
              <div>
                <label className={labelClasses}>Account Number <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('accountNumber')}
                  type="text"
                  className={`${inputClasses} ${errors.accountNumber ? 'border-red-500' : ''}`}
                />
                {errors.accountNumber && <p className="text-xs text-red-500 mt-1">{errors.accountNumber.message as string}</p>}
              </div>
              <div>
                <label className={labelClasses}>Confirm Account Number <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('confirmAccountNumber')}
                  type="text"
                  className={`${inputClasses} ${errors.confirmAccountNumber ? 'border-red-500' : ''}`}
                />
                {errors.confirmAccountNumber && <p className="text-xs text-red-500 mt-1">{errors.confirmAccountNumber.message as string}</p>}
              </div>
              <div>
                <label className={labelClasses}>Routing Number <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('routingNumber')}
                  type="text"
                  className={`${inputClasses} ${errors.routingNumber ? 'border-red-500' : ''}`}
                  maxLength={9}
                />
                {errors.routingNumber && <p className="text-xs text-red-500 mt-1">{errors.routingNumber.message as string}</p>}
              </div>
              {isOwnerBank && (
                <div>
                  <label className={labelClasses}>Distribution Frequency <span className="text-red-500">*</span></label>
                  <select
                    disabled={readOnly}
                    {...register('distributionFrequency')}
                    className={`${inputClasses} ${errors.distributionFrequency ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select...</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annually">Annually</option>
                  </select>
                  {errors.distributionFrequency && <p className="text-xs text-red-500 mt-1">{(errors.distributionFrequency as { message?: string }).message}</p>}
                </div>
              )}
            </form>
          );
        }

      case 'services':
        {
          const FULL_SERVICE_CATEGORIES = [
            'Plumbing (Licensed - Permits)',
            'Electrical (Licensed - Permits)',
            'HVAC (Licensed - Installation/Replacement)',
            'General Contractor (GC License)',
            'Roofing (Licensed - Replacement)',
            'Structural/Foundation Work',
            'Commercial Kitchen Equipment',
          ];
          const LITE_SERVICE_CATEGORIES = [
            'Plumbing - Minor Repairs (No Permits)',
            'Electrical - Minor Repairs (No Permits)',
            'HVAC - Filter/Maintenance Only',
            'Appliance Repair',
            'Carpentry - Basic Repairs',
            'Painting & Drywall Patching',
            'Flooring - Minor Repairs',
            'Door & Lock Repair',
            'Window Repair',
            'Fixture Replacement',
            'Weatherproofing/Caulking',
            'Furniture Assembly/Installation',
          ];
          const serviceLevel = watch('serviceLevel') as 'full-service' | 'lite' | 'both' | undefined;
          const showFull = serviceLevel === 'full-service' || serviceLevel === 'both';
          const showLite = serviceLevel === 'lite' || serviceLevel === 'both';
          const fullSelected = (watch('fullServiceCategories') as string[]) ?? [];
          const liteSelected = (watch('liteServiceCategories') as string[]) ?? [];

          const toggleCategory = (key: 'fullServiceCategories' | 'liteServiceCategories', value: string) => {
            const current = (watch(key) as string[]) ?? [];
            const next = current.includes(value) ? current.filter((s: string) => s !== value) : [...current, value];
            setValue(key, next, { shouldValidate: true });
          };

          return (
            <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
              {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
                <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
              )}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Full-Service requires licenses and insurance for permitted work. Lite is for general maintenance and repairs.
                </p>
              </div>

              <div>
                <label className={labelClasses}>Service level <span className="text-red-500">*</span></label>
                <div className="space-y-2">
                  {[
                    { value: 'full-service', label: 'Full-Service (Licensed/Permitted Work)' },
                    { value: 'lite', label: 'Lite Service (Handyman/Maintenance)' },
                    { value: 'both', label: 'Both Full-Service & Lite' },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        disabled={readOnly}
                        type="radio"
                        value={opt.value}
                        {...register('serviceLevel')}
                        className="w-4 h-4 text-brand-600 border-slate-300 focus:ring-brand-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{opt.label}</span>
                    </label>
                  ))}
                </div>
                {errors.serviceLevel && <p className="text-xs text-red-500 mt-1">{(errors.serviceLevel as { message?: string }).message}</p>}
              </div>

              {showFull && (
                <div>
                  <label className={labelClasses}>Full-Service categories</label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Requires valid licenses, permits, and higher insurance coverage</p>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
                    {FULL_SERVICE_CATEGORIES.map((cat) => (
                      <label key={cat} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 p-2 rounded">
                        <input
                          disabled={readOnly}
                          type="checkbox"
                          checked={fullSelected.includes(cat)}
                          onChange={() => toggleCategory('fullServiceCategories', cat)}
                          className="w-4 h-4 text-brand-600 border-slate-300 rounded focus:ring-brand-500"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{cat}</span>
                      </label>
                    ))}
                  </div>
                  {errors.fullServiceCategories && <p className="text-xs text-red-500 mt-1">{(errors.fullServiceCategories as { message?: string }).message}</p>}
                </div>
              )}

              {showLite && (
                <div>
                  <label className={labelClasses}>Lite service categories</label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">General maintenance and repairs that typically don&apos;t require permits</p>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
                    {LITE_SERVICE_CATEGORIES.map((cat) => (
                      <label key={cat} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 p-2 rounded">
                        <input
                          disabled={readOnly}
                          type="checkbox"
                          checked={liteSelected.includes(cat)}
                          onChange={() => toggleCategory('liteServiceCategories', cat)}
                          className="w-4 h-4 text-brand-600 border-slate-300 rounded focus:ring-brand-500"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{cat}</span>
                      </label>
                    ))}
                  </div>
                  {errors.liteServiceCategories && <p className="text-xs text-red-500 mt-1">{(errors.liteServiceCategories as { message?: string }).message}</p>}
                </div>
              )}
            </form>
          );
        }

      case 'emergency':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div>
              <label className={labelClasses}>Emergency Contact Name <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register("emergencyContactName")}
                type="text"
                autoComplete="name"
                className={`${inputClasses} ${errors.emergencyContactName ? 'border-red-500' : ''}`}
              />
              {errors.emergencyContactName && <p className="text-xs text-red-500 mt-1">{errors.emergencyContactName.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Emergency Contact Phone <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register("emergencyContactPhone")}
                type="tel"
                autoComplete="tel"
                className={`${inputClasses} ${errors.emergencyContactPhone ? 'border-red-500' : ''}`}
              />
              {errors.emergencyContactPhone && <p className="text-xs text-red-500 mt-1">{errors.emergencyContactPhone.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Relationship <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register("emergencyContactRelation")}
                type="text"
                autoComplete="off"
                className={`${inputClasses} ${errors.emergencyContactRelation ? 'border-red-500' : ''}`}
                placeholder="e.g. Spouse, Parent, Friend"
              />
              {errors.emergencyContactRelation && <p className="text-xs text-red-500 mt-1">{errors.emergencyContactRelation.message as string}</p>}
            </div>
          </form>
        );

      case 'portal':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div>
              <label className={labelClasses}>Username <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register("username")}
                type="text"
                autoComplete="username"
                className={`${inputClasses} ${errors.username ? 'border-red-500' : ''}`}
              />
              {errors.username && <p className="text-xs text-red-500 mt-1">{errors.username.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Password <span className="text-red-500">*</span></label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">At least 8 characters.</p>
              <input
                disabled={readOnly}
                {...register("password")}
                type="password"
                autoComplete="new-password"
                className={`${inputClasses} ${errors.password ? 'border-red-500' : ''}`}
              />
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Confirm Password <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register("confirmPassword")}
                type="password"
                autoComplete="new-password"
                className={`${inputClasses} ${errors.confirmPassword ? 'border-red-500' : ''}`}
              />
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword.message as string}</p>}
            </div>
          </form>
        );

      case 'approval':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Ready to Submit</h3>
              <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-4">
                Your application will be sent to the Property Manager for review. You will be notified via email once approved.
              </p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-left text-sm text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                <ul className="space-y-2">
                  <li className="flex items-center"><CheckCircle size={14} className="text-emerald-500 mr-2" /> Business Info provided</li>
                  <li className="flex items-center"><CheckCircle size={14} className="text-emerald-500 mr-2" /> Insurance uploaded</li>
                  <li className="flex items-center"><CheckCircle size={14} className="text-emerald-500 mr-2" /> W-9 attached</li>
                  <li className="flex items-center"><CheckCircle size={14} className="text-emerald-500 mr-2" /> Bank details secure</li>
                </ul>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input disabled={readOnly} type="checkbox" {...register('backgroundCheck')} className="w-4 h-4 rounded border-slate-300" />
              <span className="text-sm text-slate-700 dark:text-slate-300">I consent to a background check <span className="text-red-500">*</span></span>
            </label>
            {errors.backgroundCheck && <p className="text-xs text-red-500 mt-1">{(errors.backgroundCheck as { message?: string }).message}</p>}
            <div>
              <label className={labelClasses}>Create password (minimum 8 characters) <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('password')}
                type="password"
                autoComplete="new-password"
                className={`${inputClasses} ${errors.password ? 'border-red-500' : ''}`}
                placeholder="Minimum 8 characters"
              />
              {errors.password && <p className="text-xs text-red-500 mt-1">{(errors.password as { message?: string }).message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Confirm password <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('confirmPassword')}
                type="password"
                autoComplete="new-password"
                className={`${inputClasses} ${errors.confirmPassword ? 'border-red-500' : ''}`}
                placeholder="Re-enter password"
              />
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{(errors.confirmPassword as { message?: string }).message}</p>}
            </div>
            <div className="flex items-start gap-2">
              <input
                id="approval-vendor-termsAccepted"
                disabled={readOnly}
                type="checkbox"
                {...register('termsAccepted')}
                aria-describedby="approval-vendor-consent-desc"
                className="w-4 h-4 rounded border-slate-300 mt-0.5 shrink-0"
              />
              <div id="approval-vendor-consent-desc" className="text-sm text-slate-700 dark:text-slate-300">
                <label htmlFor="approval-vendor-termsAccepted" className="cursor-pointer">
                  I agree to the
                </label>{' '}
                <a
                  href={ROUTES.LEGAL_TERMS}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href={ROUTES.LEGAL_PRIVACY}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                <span className="text-red-500"> *</span>
              </div>
            </div>
            {errors.termsAccepted && <p className="text-xs text-red-500 mt-1">{(errors.termsAccepted as { message?: string }).message}</p>}
          </form>
        );

      case 'lease':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(application || leaseByApp) && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 space-y-2 text-sm">
                <h4 className="font-semibold text-slate-800 dark:text-slate-200">Lease details (from your application)</h4>
                {application && (
                  <>
                    <p><span className="text-slate-500 dark:text-slate-400">Property:</span> <span className="font-medium">{application.propertyName ?? application.propertyId ?? '—'}</span></p>
                    <p><span className="text-slate-500 dark:text-slate-400">Unit:</span> <span className="font-medium">{application.unitNumber ?? application.floorPlanName ?? '—'}</span></p>
                    {application.moveInDate && (
                      <p><span className="text-slate-500 dark:text-slate-400">Move-in date:</span> <span className="font-medium">{formatDate(application.moveInDate, 'short')}</span></p>
                    )}
                  </>
                )}
                {leaseByApp && (
                  <>
                    <p><span className="text-slate-500 dark:text-slate-400">Lease start:</span> <span className="font-medium">{formatDate(leaseByApp.startDate, 'short')}</span></p>
                    <p><span className="text-slate-500 dark:text-slate-400">Lease end:</span> <span className="font-medium">{formatDate(leaseByApp.endDate, 'short')}</span></p>
                    <p><span className="text-slate-500 dark:text-slate-400">Lease term:</span> <span className="font-medium">{leaseByApp.leaseTerm} months</span></p>
                    <p><span className="text-slate-500 dark:text-slate-400">Monthly rent:</span> <span className="font-medium">{formatCurrency(leaseByApp.monthlyRent, { isCents: true })}</span></p>
                    {typeof leaseByApp.deposit === 'number' && leaseByApp.deposit > 0 && (
                      <p><span className="text-slate-500 dark:text-slate-400">Security deposit:</span> <span className="font-medium">{formatCurrency(leaseByApp.deposit, { isCents: false })}</span></p>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300">
              <p className="mb-3">You&apos;ll complete lease signing after your application is approved. Before move-in you&apos;ll need to upload: driver&apos;s license, social security card, proof of renters insurance, and utility confirmations.</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input disabled={readOnly} type="checkbox" {...register('acknowledge')} className="w-4 h-4 rounded border-slate-300" />
                <span>I acknowledge</span>
              </label>
            </div>
            <div>
              <label className={labelClasses}>Application or reference ID (optional)</label>
              <input disabled={readOnly || !!applicationIdProp} {...register('applicationId')} type="text" className={inputClasses} placeholder="If you have one" />
            </div>
          </form>
        );

      case 'license':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner ? (
              <>
                <label className="flex items-center gap-2 cursor-pointer mb-4">
                  <input disabled={readOnly} type="checkbox" {...register('verifyLater')} className="w-4 h-4 rounded border-slate-300" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">I&apos;ll verify my license later</span>
                </label>
                {!watch('verifyLater') && (
                  <>
                    <div>
                      <label className={labelClasses}>License State <span className="text-red-500">*</span></label>
                      <select disabled={readOnly} {...register('licenseState')} className={`${inputClasses} ${errors.licenseState ? 'border-red-500' : ''}`}>
                        <option value="">Select an option</option>
                        {US_STATES.map((s: StateOption) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      {errors.licenseState && <p className="text-xs text-red-500 mt-1">{(errors.licenseState as { message?: string }).message}</p>}
                    </div>
                    <div>
                      <label className={labelClasses}>Broker/RE License Number <span className="text-red-500">*</span></label>
                      <input disabled={readOnly} {...register('licenseNumber')} type="text" className={`${inputClasses} ${errors.licenseNumber ? 'border-red-500' : ''}`} placeholder="e.g. 12345" />
                      {errors.licenseNumber && <p className="text-xs text-red-500 mt-1">{(errors.licenseNumber as { message?: string }).message}</p>}
                    </div>
                    <div>
                      <Controller
                        control={control}
                        name="licenseExpiration"
                        render={({ field }) => (
                          <DatePicker
                            label="License Expiration Date *"
                            disabled={readOnly}
                            value={typeof field.value === 'string' ? field.value : ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            name={field.name}
                            onBlur={field.onBlur}
                            error={errors.licenseExpiration ? (errors.licenseExpiration as { message?: string }).message : undefined}
                            className={`${inputClasses} ${errors.licenseExpiration ? 'border-red-500' : ''}`}
                          />
                        )}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Upload License (PDF) <span className="text-red-500">*</span></label>
                      <div className="mt-1 flex items-center gap-2">
                        <input disabled={readOnly} type="file" accept=".pdf" {...register('licenseDocument')} className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-200" />
                        <span className="text-xs text-slate-500">No file chosen</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Digital copy of your real estate or broker license</p>
                    </div>
                    <div className="pt-2">
                      <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Professional Certifications</h4>
                      {(['CPM', 'ARM', 'CAM', 'RMP', 'MPM'] as const).map((cert) => {
                        const certLabels: Record<string, string> = {
                          CPM: 'CPM (Certified Property Manager)',
                          ARM: 'ARM (Accredited Residential Manager)',
                          CAM: 'CAM (Certified Apartment Manager)',
                          RMP: 'RMP (Residential Management Professional)',
                          MPM: 'MPM (Master Property Manager)',
                        };
                        const current = (watch('certifications') as string[] | undefined) ?? [];
                        const checked = current.includes(cert);
                        return (
                          <label key={cert} className="flex items-center gap-2 cursor-pointer py-1">
                            <input
                              disabled={readOnly}
                              type="checkbox"
                              className="w-4 h-4 rounded border-slate-300"
                              checked={checked}
                              onChange={() => {
                                const next = checked ? current.filter((c) => c !== cert) : [...current, cert];
                                setValue('certifications', next);
                              }}
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">{certLabels[cert]}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div>
                      <label className={labelClasses}>E&O Insurance (Certificate)</label>
                      <div className="mt-1 flex items-center gap-2">
                        <input disabled={readOnly} type="file" accept=".pdf,.jpg,.jpeg,.png" {...register('insuranceCOI')} className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-200" />
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Errors &amp; Omissions insurance certificate</p>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">License verification is required for ACH and escrow processing. We verify with your state board.</p>
                <p className="text-sm text-slate-500 dark:text-slate-500 mb-3">You can enter details now or verify later.</p>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input disabled={readOnly} type="checkbox" {...register('verifyLater')} className="w-4 h-4 rounded border-slate-300" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">I&apos;ll verify my license later</span>
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">You can add or update your license in Settings after signup.</p>
                {!watch('verifyLater') && (
                  <>
                    <div>
                      <label className={labelClasses}>License number</label>
                      <input disabled={readOnly} {...register('licenseNumber')} type="text" className={inputClasses} placeholder="e.g. 12345" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>State</label>
                        <select disabled={readOnly} {...register('licenseState')} className={inputClasses}>
                          <option value="">Select state</option>
                          {US_STATES.map((s: StateOption) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Controller
                          control={control}
                          name="licenseExpiration"
                          render={({ field }) => (
                            <DatePicker
                              label="Expiration date"
                              disabled={readOnly}
                              value={typeof field.value === 'string' ? field.value : ''}
                              onChange={(e) => field.onChange(e.target.value)}
                              name={field.name}
                              onBlur={field.onBlur}
                              className={inputClasses}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </form>
        );

      // --- PM / Owner: Review (summary before submit); PM includes Review & Set Password ---
      case 'review': {
        const personal = (allStepData.personal ?? {}) as Record<string, unknown>;
        const company = (allStepData.company ?? {}) as Record<string, unknown>;
        const license = (allStepData.license ?? {}) as Record<string, unknown>;
        const propertyData = (allStepData.property ?? {}) as { properties?: { name?: string; address?: string; city?: string; state?: string; type?: string; status?: string }[] };
        const propertyList = Array.isArray(propertyData.properties) ? propertyData.properties : [];
        const isOwner = role === UserRole.OWNER;
        const isPmReview = role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner;
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-6 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {isOwner ? 'Next step is creating your account with a password.' : isPmReview
                ? (embedded && onClerkSignupRequest ? 'Review your information below. You\'ll create your account on the next screen.' : 'Review your information below and create your account.')
                : "We'll set up your access; the next step is creating your account with a password."}
            </p>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Your profile</h3>
              </div>
              <div className="p-4 space-y-3 text-sm">
                {propertyList.length > 0 ? (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">Properties</span>
                    <ul className="font-medium text-slate-900 dark:text-white list-disc list-inside space-y-1">
                      {propertyList.map((p, i) => {
                        const name = String(p?.name || '').trim();
                        const address = String(p?.address || '').trim();
                        const typeStatus = [p?.type, p?.status].filter(Boolean).join(' • ');
                        const line1 = name || address || `Property ${i + 1}`;
                        const line2 = name && address ? address : typeStatus || null;
                        return (
                          <li key={i}>
                            {line1}
                            {line2 ? <span className="text-slate-600 dark:text-slate-400 block text-xs font-normal ml-4">{line2}</span> : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">Contact</span>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {[String(personal.firstName ?? ''), String(personal.lastName ?? '')].filter(Boolean).join(' ')}
                    {personal.email ? ` • ${String(personal.email)}` : ''}
                  </p>
                </div>
                {(!isOwner || isSelfManagingOwner) && (company.companyName || personal.companyName) ? (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">Company</span>
                    <p className="font-medium text-slate-900 dark:text-white">{String((company.companyName || personal.companyName) ?? '')}</p>
                  </div>
                ) : null}
                {!isOwner && !isSelfManagingOwner ? (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">License</span>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {license.verifyLater ? 'To be verified later' : [license.licenseNumber, license.licenseState, license.licenseExpiration].filter(Boolean).join(' • ') || 'Not provided'}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            {isPmReview && (
              <div className="space-y-4 pt-2 border-t border-slate-200 dark:border-slate-700">
                {!(embedded && onClerkSignupRequest) && (
                  <>
                    <div>
                      <label className={labelClasses}>Password <span className="text-red-500">*</span></label>
                      <input disabled={readOnly} {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })} type="password" autoComplete="new-password" className={`${inputClasses} ${errors.password ? 'border-red-500' : ''}`} placeholder="Minimum 8 characters" />
                      {errors.password && <p className="text-xs text-red-500 mt-1">{(errors.password as { message?: string }).message}</p>}
                    </div>
                    <div>
                      <label className={labelClasses}>Confirm Password <span className="text-red-500">*</span></label>
                      <input disabled={readOnly} {...register('confirmPassword', { required: 'Please confirm password' })} type="password" autoComplete="new-password" className={`${inputClasses} ${errors.confirmPassword ? 'border-red-500' : ''}`} placeholder="Re-enter password" />
                      {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{(errors.confirmPassword as { message?: string }).message}</p>}
                    </div>
                  </>
                )}
                <div className="flex items-start gap-2">
                  <input
                    id="pm-review-termsAccepted"
                    disabled={readOnly}
                    type="checkbox"
                    {...register('termsAccepted', { required: 'You must agree to the terms' })}
                    aria-describedby="pm-review-consent-desc"
                    className="w-4 h-4 rounded border-slate-300 mt-0.5 shrink-0"
                  />
                  <div id="pm-review-consent-desc" className="text-sm text-slate-700 dark:text-slate-300">
                    <label htmlFor="pm-review-termsAccepted" className="cursor-pointer">
                      I agree to the
                    </label>{' '}
                    <a
                      href={ROUTES.LEGAL_TERMS}
                      className="text-brand-600 dark:text-brand-400 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a
                      href={ROUTES.LEGAL_PRIVACY}
                      className="text-brand-600 dark:text-brand-400 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-red-500"> *</span>
                  </div>
                </div>
                {errors.termsAccepted && <p className="text-xs text-red-500 mt-1">{(errors.termsAccepted as { message?: string }).message}</p>}
              </div>
            )}
            {!isPmReview && <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">Use the button below to complete onboarding.</p>}
          </form>
        );
      }

      case 'permissions':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">We&apos;ll set up your permission groups with you. No action needed here.</p>
          </form>
        );

      case 'property': {
        const addNow = watch('addPropertiesNow') as 'yes' | 'later' | 'import' | undefined;
        const isPmFigma = role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner;
        return (
          <>
            <form ref={formRef} id="onboarding-form-property" onSubmit={handleSubmit(handleStepSubmit)} className="space-y-6 animate-fade-in">
              {isPmFigma ? (
                <>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Would you like to add properties now?</p>
                  <div className="space-y-3">
                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${addNow === 'yes' ? 'border-brand-600 bg-brand-50/50 dark:bg-brand-900/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                      <input type="radio" disabled={readOnly} {...register('addPropertiesNow', { required: isPmFigma ? 'Please choose an option' : false })} value="yes" className="mt-1 w-4 h-4 text-brand-600" />
                      <div>
                        <span className="font-medium text-slate-900 dark:text-white">Yes, add properties now</span>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Set up your portfolio during onboarding</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${addNow === 'later' ? 'border-brand-600 bg-brand-50/50 dark:bg-brand-900/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                      <input type="radio" disabled={readOnly} {...register('addPropertiesNow')} value="later" className="mt-1 w-4 h-4 text-brand-600" />
                      <div>
                        <span className="font-medium text-slate-900 dark:text-white">I&apos;ll add them later</span>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Complete setup first, add properties from your dashboard</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${addNow === 'import' ? 'border-brand-600 bg-brand-50/50 dark:bg-brand-900/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                      <input type="radio" disabled={readOnly} {...register('addPropertiesNow')} value="import" className="mt-1 w-4 h-4 text-brand-600" />
                      <div>
                        <span className="font-medium text-slate-900 dark:text-white">Migrate my portfolio</span>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Upload a file in the next steps and we&apos;ll migrate your portfolio as part of signup, or do it from Portfolio after signup.</p>
                      </div>
                    </label>
                  </div>
                  {addNow === 'import' && (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-4 text-sm text-slate-600 dark:text-slate-400">
                      <p>Our migration tools bring over properties, units, tenants, and leases from your current system. Upload a file in the next steps (or do it from <strong>Portfolio</strong> after signup).</p>
                    </div>
                  )}
                  {addNow === 'yes' && (
                    <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 p-6 flex flex-col items-center text-center">
                      <Building size={32} className="text-brand-600 dark:text-brand-400 mb-3" />
                      <h4 className="font-bold text-slate-900 dark:text-white">Ready to add your first property?</h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Set up property details, units, tenants, and more</p>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => setShowFullPropertyWizard(true)}
                          className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-sm"
                        >
                          <span className="text-lg leading-none">+</span> Add Your First Property
                        </button>
                      )}
                    </div>
                  )}
                  {collectedPropertiesForStep.length > 0 && (
                    <ul className="space-y-2">
                      {collectedPropertiesForStep.map((p, i) => (
                        <li key={i} className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 text-sm text-slate-700 dark:text-slate-300">
                          <Building size={16} className="shrink-0 text-slate-500" />
                          <span className="font-medium">{p.name || p.address || `Property ${i + 1}`}</span>
                          {p.address && p.name !== p.address && <span className="text-slate-500">— {p.address}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Add one or more properties using the full wizard. You can add more after signup from the Property Directory.
                  </p>
                  {collectedPropertiesForStep.length > 0 && (
                    <ul className="space-y-2">
                      {collectedPropertiesForStep.map((p, i) => (
                        <li key={i} className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 text-sm text-slate-700 dark:text-slate-300">
                          <Building size={16} className="shrink-0 text-slate-500" />
                          <span className="font-medium">{p.name || p.address || `Property ${i + 1}`}</span>
                          {p.address && p.name !== p.address && <span className="text-slate-500">— {p.address}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!readOnly && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={() => setShowFullPropertyWizard(true)}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white rounded-xl transition shadow-sm hover:shadow-md"
                      >
                        <Building size={18} /> Add property
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCollectedPropertiesForStep([]);
                          handleStepSubmit({ properties: [] });
                        }}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl transition"
                      >
                        Skip Add Property
                      </button>
                    </div>
                  )}
                </>
              )}
            </form>
            {showFullPropertyWizard && createPortal(
              <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowFullPropertyWizard(false)}>
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
                  <AddPropertyWizard
                    mode="collect"
                    onClose={() => setShowFullPropertyWizard(false)}
                    onCollected={(data) => {
                      const row: PropertyFormValues['properties'][0] = {
                        name: data.name || data.address,
                        address: data.address,
                        city: data.city,
                        state: data.state,
                        zip: data.zip,
                        yearBuilt: data.yearBuilt,
                        type: data.type,
                        status: data.status,
                      };
                      setCollectedPropertiesForStep(prev => [...prev, row]);
                      setShowFullPropertyWizard(false);
                    }}
                  />
                </div>
              </div>,
              document.body
            )}
          </>
        );
      }

      case 'migration-upload': {
        const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const hadInProgress = migrationUploadStatus === 'parsing' || migrationUploadStatus === 'uploading';
          const pollGeneration = migrationPoll.beginNewUploadAttempt();
          const stillCurrent = migrationPoll.isAttemptCurrent(pollGeneration);
          if (hadInProgress) {
            showToast('Previous upload canceled.', 'info');
          }
          const sizeMb = file.size / (1024 * 1024);
          if (sizeMb > 5) {
            setMigrationUploadError('File must be 5 MB or smaller.');
            return;
          }
          setMigrationUploadError('');
          setMigrationUploadStatus('uploading');
          try {
            const raw = await file.text();
            if (!stillCurrent()) return;
            const content = btoa(unescape(encodeURIComponent(raw)));
            const source = /\.csv$/i.test(file.name) ? 'csv' as const : 'json' as const;
            const res = await onboardingUploadApi.upload({content, source, fileName: file.name});
            if (!stillCurrent()) return;
            const token = res.upload_token;
            setMigrationUploadToken(token);
            setMigrationUploadStatus('parsing');
            const poll = async () => {
              for (let i = 0; i < 60; i++) {
                if (!stillCurrent()) return;
                const st = await onboardingUploadApi.status(token);
                if (!stillCurrent()) return;
                if (st.status === 'ready') {
                  setMigrationUploadStatus('ready');
                  setValue('upload_token', token);
                  return;
                }
                if (st.status === 'failed') {
                  setMigrationUploadStatus('failed');
                  setMigrationUploadError('Parsing failed. Please check the file format.');
                  return;
                }
                await new Promise((r) => setTimeout(r, 2000));
              }
              if (!stillCurrent()) return;
              setMigrationUploadStatus('failed');
              setMigrationUploadError('Upload timed out.');
            };
            await poll();
          } catch (err) {
            if (!stillCurrent()) return;
            setMigrationUploadStatus('failed');
            setMigrationUploadError(formatMigrationUploadFailure(err));
          }
        };
        return (
          <form ref={formRef} id="onboarding-form-migration-upload" onSubmit={handleSubmit(handleStepSubmit)} className="space-y-6 animate-fade-in">
            <p className="text-sm text-slate-600 dark:text-slate-400">Upload a CSV or JSON file (max 5 MB). We&apos;ll parse it and show a summary before you continue.</p>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Portfolio file</label>
              <input
                type="file"
                accept=".csv,.json"
                onChange={handleFileChange}
                disabled={migrationUploadStatus === 'uploading' || migrationUploadStatus === 'parsing'}
                className="block w-full text-sm text-slate-600 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 dark:file:bg-brand-900/30 dark:file:text-brand-300 hover:file:bg-brand-100 dark:hover:file:bg-brand-900/50"
              />
            </div>
            {(migrationUploadStatus === 'uploading' || migrationUploadStatus === 'parsing') && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                <LoadingSpinner className="w-5 h-5" />
                <span>{migrationUploadStatus === 'uploading' ? 'Uploading…' : 'Parsing…'}</span>
              </div>
            )}
            {migrationUploadStatus === 'ready' && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200">
                <CheckCircle size={20} className="shrink-0" />
                <span>File parsed. Click Next to review the data.</span>
              </div>
            )}
            {migrationUploadStatus === 'failed' && migrationUploadError && (
              <p className="text-sm text-red-600 dark:text-red-400">{migrationUploadError}</p>
            )}
          </form>
        );
      }

      case 'migration-review': {
        const token = (allStepData['migration-upload'] as { upload_token?: string } | undefined)?.upload_token ?? '';
        const rawPayload = migrationReviewData?.parsed_payload;
        const payload: ParsedPayload | undefined =
          rawPayload != null && typeof rawPayload === 'object' && !Array.isArray(rawPayload)
            ? (rawPayload as ParsedPayload)
            : undefined;
        const exceptions = migrationReviewData?.exceptions ?? [];
        return (
          <form ref={formRef} id="onboarding-form-migration-review" onSubmit={handleSubmit(handleStepSubmit)} className="space-y-6 animate-fade-in">
            {migrationReviewLoading && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-100 dark:bg-slate-800">
                <LoadingSpinner className="w-5 h-5" />
                <span>Loading review…</span>
              </div>
            )}
            {migrationReviewError && <p className="text-sm text-red-600 dark:text-red-400">{migrationReviewError}</p>}
            {!migrationReviewLoading && !migrationReviewError && (token && payload ? (
              <MigrationReviewContent
                payload={payload}
                exceptions={exceptions}
                setFormValue={setValue}
                uploadToken={token}
                onReviewDataChange={handleMigrationReviewDataChange}
              />
            ) : !migrationReviewLoading && !migrationReviewError ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No upload data. Go back to upload a file.</p>
            ) : null)}
          </form>
        );
      }

      case 'tax':
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
              <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
            )}
            <div>
              <label className={labelClasses}>Tax ID Type <span className="text-red-500">*</span></label>
              <select
                disabled={readOnly}
                {...register('taxIdType')}
                className={`${inputClasses} ${errors.taxIdType ? 'border-red-500' : ''}`}
              >
                <option value="SSN">Social Security Number (SSN)</option>
                <option value="EIN">Employer ID Number (EIN)</option>
              </select>
              {errors.taxIdType && <p className="text-xs text-red-500 mt-1">{errors.taxIdType.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>Tax ID <span className="text-red-500">*</span></label>
              <input
                disabled={readOnly}
                {...register('taxId')}
                type="text"
                className={`${inputClasses} ${errors.taxId ? 'border-red-500' : ''}`}
                placeholder="XXX-XX-XXXX or XX-XXXXXXX"
              />
              {errors.taxId && <p className="text-xs text-red-500 mt-1">{errors.taxId.message as string}</p>}
            </div>
            <div>
              <label className={labelClasses}>W-9 upload (optional)</label>
              <input
                disabled={readOnly}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                {...register('w9Upload')}
                className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-200"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Upload a completed W-9 form</p>
            </div>
          </form>
        );

      case 'terms':
        if (role === UserRole.INVESTOR) {
          return (
            <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
              {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
                <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
              )}
              <div>
                <label className={labelClasses}>Offering code <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('offeringCode')}
                  type="text"
                  className={`${inputClasses} ${errors.offeringCode ? 'border-red-500' : ''}`}
                  placeholder="e.g. SUN-2024-01"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Provided in your investment invitation</p>
                {errors.offeringCode && <p className="text-xs text-red-500 mt-1">{(errors.offeringCode as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>Investment amount ($) <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  {...register('investmentAmount', { valueAsNumber: true })}
                  type="number"
                  min={50000}
                  step={1000}
                  className={`${inputClasses} ${errors.investmentAmount ? 'border-red-500' : ''}`}
                  placeholder="Minimum $50,000"
                />
                {errors.investmentAmount && <p className="text-xs text-red-500 mt-1">{(errors.investmentAmount as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>Investment type <span className="text-red-500">*</span></label>
                <select
                  disabled={readOnly}
                  {...register('investmentType')}
                  className={`${inputClasses} ${errors.investmentType ? 'border-red-500' : ''}`}
                >
                  <option value="">Select...</option>
                  <option value="Equity">Equity</option>
                  <option value="Debt">Debt</option>
                  <option value="Preferred Equity">Preferred Equity</option>
                  <option value="Mezzanine">Mezzanine</option>
                </select>
                {errors.investmentType && <p className="text-xs text-red-500 mt-1">{(errors.investmentType as { message?: string }).message}</p>}
              </div>
            </form>
          );
        }
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">I agree to the investor terms and conditions.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input disabled={readOnly} type="checkbox" {...register('agreedToTerms')} className="w-4 h-4 rounded border-slate-300" />
              <span>I agree</span>
            </label>
          </form>
        );

      case 'accreditation':
        if (role === UserRole.INVESTOR) {
          const ACCREDITATION_TYPES = [
            'Income ($200k individual / $300k joint for past 2 years)',
            'Net Worth (Over $1M excluding primary residence)',
            'Professional Certification (Series 7, 65, 82)',
            'Entity with $5M+ in assets',
          ];
          return (
            <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
              {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
                <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
              )}
              <div>
                <label className={labelClasses}>Accreditation basis <span className="text-red-500">*</span></label>
                <select
                  disabled={readOnly}
                  {...register('accreditationType')}
                  className={`${inputClasses} ${errors.accreditationType ? 'border-red-500' : ''}`}
                >
                  <option value="">Select...</option>
                  {ACCREDITATION_TYPES.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {errors.accreditationType && <p className="text-xs text-red-500 mt-1">{(errors.accreditationType as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>Verification document <span className="text-red-500">*</span></label>
                <input
                  disabled={readOnly}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200"
                  {...register('accreditationDocument')}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Tax returns, CPA letter, or professional license</p>
                {errors.accreditationDocument && <p className="text-xs text-red-500 mt-1">{(errors.accreditationDocument as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>CPA verification letter (optional)</label>
                <input
                  disabled={readOnly}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200"
                  {...register('cpaLetter')}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Letter from CPA confirming accreditation status</p>
              </div>
            </form>
          );
        }
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            <div>
              <label className={labelClasses}>Accreditation status</label>
              <input disabled={readOnly} {...register('status')} type="text" className={inputClasses} placeholder="e.g. Accredited investor" />
            </div>
            <div>
              <label className={labelClasses}>Notes (optional)</label>
              <input disabled={readOnly} {...register('notes')} type="text" className={inputClasses} />
            </div>
          </form>
        );

      case 'legal':
        if (role === UserRole.INVESTOR) {
          return (
            <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
              {(isSubmitted || Object.keys(touchedFields).length > 0) && Object.keys(errors).length > 0 && (
                <FormErrorSummary errors={errors} formId={`onboarding-form-${currentStepData.id}`} />
              )}
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input disabled={readOnly} type="checkbox" {...register('ppmReviewed')} className="w-4 h-4 rounded border-slate-300 mt-0.5" />
                  <span>I have reviewed the Private Placement Memorandum (PPM)</span>
                </label>
                {errors.ppmReviewed && <p className="text-xs text-red-500">{(errors.ppmReviewed as { message?: string }).message}</p>}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input disabled={readOnly} type="checkbox" {...register('operatingAgreement')} className="w-4 h-4 rounded border-slate-300 mt-0.5" />
                  <span>I agree to the Operating Agreement terms</span>
                </label>
                {errors.operatingAgreement && <p className="text-xs text-red-500">{(errors.operatingAgreement as { message?: string }).message}</p>}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input disabled={readOnly} type="checkbox" {...register('subscriptionAgreement')} className="w-4 h-4 rounded border-slate-300 mt-0.5" />
                  <span>I agree to the Subscription Agreement</span>
                </label>
                {errors.subscriptionAgreement && <p className="text-xs text-red-500">{(errors.subscriptionAgreement as { message?: string }).message}</p>}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input disabled={readOnly} type="checkbox" {...register('riskAcknowledgment')} className="w-4 h-4 rounded border-slate-300 mt-0.5" />
                  <span>I understand the risks involved in this investment</span>
                </label>
                {errors.riskAcknowledgment && <p className="text-xs text-red-500">{(errors.riskAcknowledgment as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>Electronic signature <span className="text-red-500">*</span></label>
                <input disabled={readOnly} {...register('signature')} type="text" autoComplete="name" className={`${inputClasses} ${errors.signature ? 'border-red-500' : ''}`} placeholder="Type your full legal name" />
                {errors.signature && <p className="text-xs text-red-500 mt-1">{(errors.signature as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>Password <span className="text-red-500">*</span></label>
                <input disabled={readOnly} {...register('password')} type="password" autoComplete="new-password" className={`${inputClasses} ${errors.password ? 'border-red-500' : ''}`} placeholder="Minimum 8 characters" />
                {errors.password && <p className="text-xs text-red-500 mt-1">{(errors.password as { message?: string }).message}</p>}
              </div>
              <div>
                <label className={labelClasses}>Confirm password <span className="text-red-500">*</span></label>
                <input disabled={readOnly} {...register('confirmPassword')} type="password" autoComplete="new-password" className={`${inputClasses} ${errors.confirmPassword ? 'border-red-500' : ''}`} placeholder="Re-enter password" />
                {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{(errors.confirmPassword as { message?: string }).message}</p>}
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input disabled={readOnly} type="checkbox" {...register('termsAccepted')} className="w-4 h-4 rounded border-slate-300 mt-0.5" />
                <span>I agree to the Platform Terms of Service</span>
              </label>
              {errors.termsAccepted && <p className="text-xs text-red-500">{(errors.termsAccepted as { message?: string }).message}</p>}
            </form>
          );
        }
        return (
          <form ref={formRef} id={`onboarding-form-${currentStepData.id}`} onSubmit={handleSubmit(handleStepSubmit)} className="space-y-4 animate-fade-in">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Legal sign-off. We&apos;ll send you the documents to complete.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input disabled={readOnly} type="checkbox" {...register('signOffAcknowledged')} className="w-4 h-4 rounded border-slate-300" />
              <span>I acknowledge</span>
            </label>
          </form>
        );

      // --- GENERIC FALLBACK ---
      default:
        return (
          <div className="text-center py-12 text-slate-400 animate-fade-in">
            <div className="mb-4 bg-slate-50 dark:bg-slate-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
              <Briefcase size={24} />
            </div>
            <p>Step content for <strong>{currentStepData.label}</strong> goes here.</p>
          </div>
        );
    }
  };

  const stepContent = renderStepContent();
  const headerTitle = getOnboardingHeaderTitle(readOnly, !!isSelfManagingOwner, role);
  const primaryFooterLabel = getOnboardingPrimaryFooterLabel(
    isLastStep,
    role,
    isSelfManagingOwner,
    embedded,
    !!onClerkSignupRequest
  );

  const wizardContent = (
    <>
      <FormLoadingOverlayInline isLoading={isSubmitting} message={isLastStep ? "Submitting..." : "Saving..."} />

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-900 dark:to-slate-800 border-b border-slate-200 dark:border-slate-800 px-6 pt-5 pb-4">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3">
              {onBack && (
                <button type="button" onClick={onBack} className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition flex items-center gap-1" aria-label="Back to role selection">
                  <ChevronLeft size={20} /> Back
                </button>
              )}
              <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {headerTitle}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {readOnly ? 'Viewing current progress' : `Step ${safeStep + 1} of ${steps.length}: ${currentStepData.label}`}
                    {!readOnly && steps.length > 0 && (
                      <span className="ml-2 text-brand-600 dark:text-brand-400 font-medium">
                        ({Math.round(((safeStep + 1) / steps.length) * 100)}% complete)
                      </span>
                    )}
                  </p>
                </div>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={handleWizardClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition" aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
          </div>
          {!readOnly && steps.length > 0 && (
            <div className="mb-3">
              <div className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-brand-600 dark:bg-brand-500 rounded-full transition-all duration-300"
                  style={{ width: `${((safeStep + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          <nav aria-label="Onboarding progress" className="w-full">
            {!readOnly && steps.length > 0 ? (
              <p className="sr-only" aria-live="polite" aria-atomic="true">
                {progressLiveMessage}
              </p>
            ) : null}
            <ol className="flex items-center gap-0 list-none p-0 m-0 w-full">
              {steps.map((step, idx) => {
                const isActive = safeStep === idx;
                const isDone = safeStep > idx;
                const shortLabel = getOnboardingStepShortLabel(step.id, step.label);
                let stepCircleClass = 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400';
                if (isDone) {
                  stepCircleClass = 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900';
                } else if (isActive) {
                  stepCircleClass = 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 ring-2 ring-slate-300 dark:ring-slate-600';
                }
                const statusLabel = isDone ? `${step.label}, completed` : isActive ? `${step.label}, current step` : `${step.label}, not started`;
                return (
                  <li
                    key={step.id}
                    className="flex flex-1 items-start gap-0 min-w-0 list-none"
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={statusLabel}
                  >
                    <div className="flex flex-col items-center flex-1 min-w-[2.75rem] max-w-[4rem]">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors shrink-0 ${stepCircleClass}`}
                      >
                        {isDone ? <CheckCircle size={12} aria-hidden /> : <span aria-hidden>{idx + 1}</span>}
                      </div>
                      <span className={`text-[11px] mt-1.5 font-medium text-center leading-tight break-words px-0.5 ${isActive ? 'text-slate-900 dark:text-white font-semibold' : 'text-slate-600 dark:text-slate-400'}`} title={step.label}>
                        {shortLabel}
                      </span>
                    </div>
                    {idx < steps.length - 1 ? (
                      <div
                        className={`flex-1 h-0.5 mx-0.5 mb-5 min-w-[6px] self-start mt-3.5 ${isDone ? 'bg-slate-900 dark:bg-slate-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                        aria-hidden
                      />
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>

        <div className={`p-6 overflow-y-auto min-h-0 max-h-[60vh] bg-white dark:bg-slate-900 [scrollbar-gutter:stable] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}>
          {isCompleted ? (
            <div className="space-y-6 text-center animate-fade-in">
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={48} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Onboarding Complete!</h2>
                <p className="text-slate-500 dark:text-slate-400">Your {role === UserRole.TENANT ? 'tenant' : role.toLowerCase()} profile has been set up successfully.</p>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 space-y-4 text-left">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">What&apos;s Next</h3>
                {(role === UserRole.PROPERTY_MANAGER || role === UserRole.OWNER || role === UserRole.VENDOR) && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">Redirecting you to your dashboard...</p>
                )}
                {role === UserRole.TENANT && (
                  <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                    <div className="flex items-start">
                      <CheckCircle size={16} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Your account is ready. You can now make payments, submit maintenance requests, and access your lease documents.</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle size={16} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Set up auto-pay in the Payments section to ensure timely rent payments.</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle size={16} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Review your lease documents in the Documents section.</span>
                    </div>
                  </div>
                )}
                {role === UserRole.INVESTOR && (
                  <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                    <div className="flex items-start">
                      <CheckCircle size={16} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Your investor profile is being reviewed. You&apos;ll receive an email once approved.</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle size={16} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Once approved, you&apos;ll have access to portfolio performance and investment opportunities.</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle size={16} className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Check your email for account access and investment documentation.</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {!readOnly && hasDraft && (
                <DraftBanner
                  hasDraft={hasDraft}
                  lastSaved={lastSaved}
                  hideWhenRecentMs={60_000}
                  onResume={() => {
                    restoreDraft();
                  }}
                  onDiscard={() => {
                    clearDraft();
                  }}
                />
              )}
              <div key={currentStepData.id}>
                {role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner && safeStep > 0 && PM_STEP_TITLE_DESCRIPTION[currentStepData.id] && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      {PM_STEP_TITLE_DESCRIPTION[currentStepData.id].title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {PM_STEP_TITLE_DESCRIPTION[currentStepData.id].description}
                    </p>
                  </div>
                )}
                {role === UserRole.OWNER && safeStep > 0 && OWNER_STEP_TITLE_DESCRIPTION[currentStepData.id] && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      {OWNER_STEP_TITLE_DESCRIPTION[currentStepData.id].title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {OWNER_STEP_TITLE_DESCRIPTION[currentStepData.id].description}
                    </p>
                  </div>
                )}
                {role === UserRole.INVESTOR && safeStep > 0 && INVESTOR_STEP_TITLE_DESCRIPTION[currentStepData.id] && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      {INVESTOR_STEP_TITLE_DESCRIPTION[currentStepData.id].title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {INVESTOR_STEP_TITLE_DESCRIPTION[currentStepData.id].description}
                    </p>
                  </div>
                )}
                {role === UserRole.VENDOR && safeStep > 0 && VENDOR_STEP_TITLE_DESCRIPTION[currentStepData.id] && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      {VENDOR_STEP_TITLE_DESCRIPTION[currentStepData.id].title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {VENDOR_STEP_TITLE_DESCRIPTION[currentStepData.id].description}
                    </p>
                  </div>
                )}
                {stepContent}
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 dark:border-slate-800 flex justify-between bg-slate-50 dark:bg-slate-900">
          {isCompleted ? (
            <div className="w-full flex justify-center">
              <button
                onClick={() => handleCompletionClose()}
                className="px-8 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white rounded-lg font-bold shadow-sm flex items-center transition"
              >
                Continue to Dashboard <ChevronRight size={16} className="ml-2" />
              </button>
            </div>
          ) : (
            <>
              {safeStep > 0 && (
                <button
                  onClick={handleBack}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ChevronLeft size={16} className="mr-2" />
                  {role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner ? 'Previous' : 'Back'}
                </button>
              )}
              {safeStep === 0 && <div className="min-w-0 flex-1" aria-hidden="true" />}

              {readOnly ? (
                <button
                  onClick={handleSendReminder}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-amber-600 text-white rounded-lg font-bold hover:bg-amber-700 shadow-sm flex items-center disabled:opacity-70 transition"
                >
                  {isSubmitting ? <LoadingSpinner size="sm" className="mr-2" /> : <Send size={16} className="mr-2" />}
                  Send Reminder
                </button>
              ) : (
              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white rounded-lg font-bold shadow-sm flex items-center disabled:opacity-70 transition"
              >
                  {isSubmitting && <LoadingSpinner size="sm" className="mr-2" />}
                  {primaryFooterLabel}
                  {!isSubmitting && !isLastStep && <ChevronRight size={16} className="ml-2" />}
                </button>
              )}
            </>
          )}
        </div>

    </>
  );
  return embedded ? (
    <div className="flex flex-col min-h-0 max-h-[85vh] overflow-hidden relative" data-testid="signup-wizard-modal">{wizardContent}</div>
  ) : (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4">
      <div className={`${cardClasses} relative`} data-testid="signup-wizard-modal">{wizardContent}</div>
    </div>
  );
};



